"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAccountModal } from "@/contexts/account-modal-context";
import { useAuth } from "@/contexts/auth-context";
import { useHydrated } from "@/hooks/use-hydrated";
import {
  closeLiveSession,
  joinLiveSession,
  openLiveSession,
  type LiveRecitalRecording,
  type LiveRole,
  type LiveSessionSummary,
  type LiveSessionTicket,
} from "@/lib/api/live-api";
import { openRecital, stopRecitalRecording } from "@/lib/api/recitals-api";
import { LaneLiveBridge } from "@/lib/live/bridge/lane-live-bridge";
import { ClockAligner, REALIGN_INTERVAL_MS, measureClockOffset } from "@/lib/live/clock-sync";
import {
  canEmit,
  ownDelayMs,
  positionNowMs,
  roomHeadcount,
  type LiveSessionState,
} from "@/lib/live/session-state";
import {
  announcePlayout,
  fetchLiveParticipants,
  excludeParticipant,
  muteParticipant,
  retractChatMessage,
  sendChatMessage,
  sendReactions,
  sendJamMode,
  sendNextTake,
  sendSessionSettings,
  sendLead,
  sendSessionCommand,
  sendSessionSignature,
  sendSessionTempo,
  type LiveParticipantsReply,
  removeLiveCamera,
} from "@/lib/live/session-control";
import { PrivateDelay } from "@/lib/live/private-delay";
import { LiveControler } from "@/lib/track/controler/live-controler";
import { StudioSession } from "@/lib/studio/session/studio-session";
import {
  liveJoinUrl,
  liveRoomFromParams,
  liveStudioPath,
  recitalIdFromParams,
  recitalStudioUrl,
} from "@/lib/live/live-link";
import { forgetRecitalEntry, recitalEntryRoom, rememberExclusion } from "@/lib/live/recital-entry";
import { recitalPath } from "@/lib/live/recital-routes";
import { exitOnDrop } from "@/lib/live/session-drop";
import type { LocalAudioLatency } from "@/lib/live/live-latency";
import { resetPlayoutOverruns } from "@/lib/live/note-trace";
import { resetTransit } from "@/lib/live/transit";
import { LiveSession } from "@/lib/live/live-session";
import { LiveHall } from "@/lib/live/hall";
import { registerLiveSession } from "@/lib/live/live-debug";
import { fetchDeclaredCameras, type DeclaredCameras } from "@/lib/live/video/camera-capabilities";
import {
  isDefaultFraming,
  type CameraFraming,
  type CameraFramings,
} from "@/lib/live/video/camera-framing";
import { WebAudioOutput } from "@/lib/live/audio/web-audio-output";
import type { LiveConnectionState, LiveParticipant } from "@/lib/live/live-transport";
import { LiveKitTransport } from "@/lib/live/livekit/livekit-transport";
import { describeCameraFailure } from "@/lib/live/camera-failure";
import { cutExtraSources, pairedDevices, recitalPairing } from "@/lib/live/recital/recital-video";
import { VideoEngineClosedError } from "@/lib/live/video/video-engine";
import type { LocalCamera, VideoPairingLink, VideoQuality, VideoSource } from "@/lib/live/video/video-types";
import { DelayedLine } from "@/lib/live/delayed-line";
import { carryEntryGrid } from "@/lib/live/entry-grid";
import {
  buildSessionTimeline,
  proposedBpm,
  sessionBpm,
  sessionTempoKey,
  SESSION_PPQ,
} from "@/lib/live/session-tempo";
import type { MusicalTimeline } from "@/lib/music/musical-timeline";
import type { Signature, Studio } from "@/lib/studio/studio";
import { transportDelayMs } from "@/lib/live/listener-horizon";
import { getToneLookAhead } from "@/lib/audio/tone-context";
import { holdSamplerLayers } from "@/lib/audio/sampler-layer-hold";
import { radioKeepaliveMs } from "@/lib/live/radio-keepalive";
import { useStudio } from "./studio-context";
import { isStudioPathname } from "@/lib/studio/studio-routes";

/**
 * Le live, vu de React.
 *
 * # Ce que ce fichier fait, et ce qu'il ne fait pas
 *
 * Il ne contient aucune logique de session : celle-ci vit dans `lib/live/`, qui
 * ne connaît ni React ni le studio. Ce contexte n'est qu'une monture — il crée
 * la session, la branche au piano, expose son état sous une forme que des
 * composants peuvent lire, et la démonte proprement.
 *
 * La frontière est utile au-delà du principe : elle est ce qui permet de tester
 * le moteur sans navigateur, et ce qui permettrait d'ouvrir une session
 * ailleurs que dans le studio — une page de démonstration, une coque légère —
 * sans rien réécrire.
 *
 * # Rejoindre se lit dans l'URL
 *
 * Il n'y a pas d'écran « rejoindre » : la présence du paramètre `?live=` suffit,
 * et le studio s'y connecte de lui-même. C'est ce qui fait qu'un lien collé dans
 * une conversation *est* l'invitation, sans page intermédiaire.
 *
 * # On n'ouvre plus rien d'ici
 *
 * Depuis le 2026-09-14, le live, c'est le récital : une salle s'ouvre quand
 * l'interprète commence son récital, depuis sa salle d'attente. Ce contexte ne
 * fait plus que rejoindre — par l'adresse que la salle d'attente a posée — et
 * quitter.
 *
 * # Et il faut un compte
 *
 * Depuis le 2026-09-11, rejoindre exige d'être connecté — l'API refuse la
 * route autrement. La garde est posée ici, sur le paramètre d'URL que personne
 * ne clique.
 *
 * Un lien reçu sans compte n'est donc pas un cul-de-sac : le paramètre reste
 * dans l'URL, l'écran de compte s'ouvre, et la jointure part d'elle-même une
 * fois l'inscription faite — l'effet rejoue sur `isAuthenticated`. C'est ce qui
 * garde à l'invitation sa valeur d'invitation.
 */

/**
 * Les attentes entre deux tentatives de mise en marche, en millisecondes.
 *
 * # Pourquoi insister
 *
 * Une séance naît figée côté service et **rien d'autre** que cette mise en
 * marche ne peut la lancer : il n'y a pas de geste de transport dans le studio,
 * et la métadonnée ne changera plus d'elle-même. Un seul envoi perdu — service
 * qui redémarre, réseau qui hoquette, jeton pas encore accepté — laissait donc
 * la séance immobile pour de bon, sans que rien ne le dise.
 *
 * Quatre tentatives sur une dizaine de secondes, puis on renonce en le
 * signalant. On ne boucle pas indéfiniment : passé ce délai, l'échec est de
 * ceux qu'une insistance ne règle pas (origine refusée, jeton invalide), et
 * c'est un message à l'écran qu'il faut, pas du trafic.
 */
const START_RETRY_DELAYS_MS = [500, 1_500, 4_000, 8_000];

/**
 * Ce que le départ de l'interprète attend, au plus, que le service coupe ses
 * téléphones. Injoignable, le service ne doit pas bloquer le départ : on part
 * quand même, et un téléphone quitte de lui-même la salle 30 s après son
 * propriétaire.
 */
const LEAVE_CAMERA_CUT_MS = 3_000;

/**
 * Ce qu'on affiche quand la mise en marche n'aboutit pas.
 *
 * Volontairement sans cause : d'ici, on ne sait pas si le service a refusé ou
 * si la requête n'est même pas partie — le navigateur ne dit pas au script
 * qu'un pré-vol CORS a échoué. Une première version affirmait « le service a
 * refusé » et envoyait chercher au mauvais endroit. Le motif est dans la
 * console (cf. `sendSessionCommand`) et dans le journal du service.
 */
const START_FAILURE_MESSAGE =
  "Le récital n'a pas pu être lancé. Le motif est dans la console du navigateur.";

export type LiveStatus =
  | "off"
  | "opening"
  | "connecting"
  | "live"
  | "reconnecting"
  | "error";

export interface LiveSessionContextValue {
  status: LiveStatus;
  /** Salle en cours, `null` hors session. */
  room: string | null;
  /** Lien à transmettre. `null` tant qu'aucune session n'est ouverte. */
  shareUrl: string | null;
  /**
   * La séance telle que l'API la connaît : son nom, sa visibilité, son code,
   * et si elle est à nous. `null` hors séance, et aussi en séance quand l'API
   * n'en sait rien (voir `LiveSessionTicket.session`).
   *
   * Exposée pour deux choses que le bandeau ne peut pas déduire : afficher le
   * **code** d'une séance privée sous le lien — il n'est lisible nulle part
   * ailleurs une fois la modale d'ouverture refermée — et savoir, en
   * partant, s'il faut la fermer.
   */
  session: LiveSessionSummary | null;
  /**
   * Les autres participants que LiveKit nous montre. Vide tant qu'on est seul.
   *
   * La liste de ce qui sonne — une lane par partenaire — et des noms du
   * contrôle de lead. Un spectateur de récital n'y figure pas : LiveKit ne
   * l'annonce plus au reste de la salle. Pour compter, c'est `headcount` ;
   * pour savoir qui est là, la route des participants (`fetchParticipants`).
   */
  participants: readonly LiveParticipant[];
  /**
   * Combien nous sommes dans la salle, soi compris : l'audience que le service
   * publie, ou la liste de LiveKit plus soi tant qu'il ne la publie pas. Le
   * nombre des compteurs — le bouton live, l'en-tête du panneau, « N autres en
   * ligne ». Voir `roomHeadcount`.
   */
  headcount: number;
  /**
   * Demande au service qui est dans la séance, et ce que chacun coûte — les
   * mesures seulement à qui peut jouer. `null` hors séance ou sans réponse,
   * jamais d'exception. Voir `fetchLiveParticipants`, et `useLiveParticipants`
   * pour la cadence.
   */
  fetchParticipants(): Promise<LiveParticipantsReply | null>;
  /**
   * La salle qui parle : le fil et les cœurs de la séance, `null` hors séance.
   *
   * Un magasin à part du contexte, et c'est voulu : un message, et quatre
   * comptes de cœurs par seconde quand la salle s'emballe, ne doivent pas
   * repeindre tout le studio. Seuls les widgets qui les montrent s'y
   * abonnent. Du direct seulement — rien n'est gardé, ni ici ni ailleurs.
   * Voir `lib/live/hall.ts`.
   */
  hall: LiveHall | null;
  /**
   * Notre identité dans la salle, `null` hors session.
   *
   * C'est la clé sous laquelle le service range ce qu'on lui a annoncé : la
   * seule façon de se reconnaître dans la liste des participants — pour lire
   * sa propre jambe telle que le service la tient, et pour ne pas s'afficher
   * parmi les autres.
   */
  identity: string | null;
  /**
   * Ce que le billet nous fait être dans la salle, `null` hors séance.
   *
   * Lu dans le billet et nulle part ailleurs : c'est Symfony qui l'a décidé
   * en signant le jeton, et LiveKit comme le service Go tiennent chacun leur
   * garde sur le même grant. Ici il ne sert qu'à ne pas offrir ce qui serait
   * refusé — et à savoir si un téléphone en paysage regarde, ou joue.
   */
  role: LiveRole | null;
  /**
   * Spectateur d'un récital : on voit, on entend, on ne touche à rien.
   *
   * `role === "listener"`, sous un nom que les écrans peuvent lire sans
   * connaître les trois rôles. Tout ce que ce contexte refuse à un spectateur
   * — les commandes, l'émission, la navigation du rouleau — se décide sur le
   * billet, à l'endroit où le geste partirait ; ce drapeau est pour ceux qui
   * ont quelque chose à cacher.
   */
  isListener: boolean;
  /**
   * Les sources vidéo de la salle, de la plus ancienne à la plus récente,
   * telles que le moteur vidéo de la séance les rend (`LiveSession.video`).
   * Vide hors séance. Voir `docs/spec/video-engine.md`.
   */
  videoSources: readonly VideoSource[];
  /** La référence de propriétaire de ce poste dans la salle, `null` tant qu'elle n'est pas connue. */
  videoSelf: string | null;
  /** La webcam que ce poste publie, ou `null`. */
  localCamera: LocalCamera | null;
  /** Le lien d'appairage en cours, tant qu'aucun téléphone ne l'a pris. */
  phonePairing: VideoPairingLink | null;
  /** Le dernier geste de caméra qui n'a pas abouti, en mots, ou `null`. */
  cameraError: string | null;
  /**
   * Allume la webcam de ce poste, ou en change. Faux si elle n'a pas pu
   * partir, motif dans `cameraError`. Un récital montre deux caméras au plus :
   * allumée en troisième, elle coupe la plus ancienne.
   */
  startWebcam(deviceId?: string | null): Promise<boolean>;
  stopWebcam(): Promise<void>;
  /** Prépare le lien qu'un téléphone ouvrira pour filmer. Faux en cas de refus. */
  pairPhone(): Promise<boolean>;
  cancelPhonePairing(): void;
  /** Coupe un téléphone appairé, par le service. Faux s'il a refusé. */
  removeCamera(sourceId: string): Promise<boolean>;
  /**
   * Le mode Jam est-il actif, pour toute la séance. Lu dans l'état publié.
   *
   * Quand il l'est, tout le monde joue, et le service publie pour la salle un
   * délai commun — le temps qu'il faut à la note la plus lointaine pour
   * arriver, avec une marge. C'est ce qui fait que chacun entend tout au même
   * instant. Sans ça, on entend ses doigts tout de suite et son
   * partenaire en différé : on le croit en retard, on ralentit pour
   * l'attendre, il fait de même, et le tempo s'effondre.
   *
   * Ce n'est pas un réglage personnel, et ça l'a été : un interrupteur local
   * décidait si *ce* transport suivait le délai. Si l'un attend et l'autre
   * non, le défaut ci-dessus revient — chez celui qui n'attend pas, dans sa
   * propre tête. Un mode qui se règle chacun de son côté défait précisément ce
   * qu'il promet.
   *
   * L'allumer retire le lead : les deux régimes s'excluent, chez le service.
   */
  jam: boolean;
  /**
   * Demande au service d'allumer ou d'éteindre le mode Jam. Faux s'il refuse.
   *
   * Même porte que la pause : le service applique, publie, et l'interrupteur
   * reflète la métadonnée — y compris quand c'est quelqu'un d'autre qui a
   * basculé. Rien n'est appliqué localement en attendant la réponse.
   */
  setJam(on: boolean): Promise<boolean>;
  /**
   * L'identité du leader, vide quand personne ne mène. Lue dans l'état publié.
   *
   * Hors jam, une personne joue et les autres écoutent : seules ses notes
   * circulent, chaque auditeur recule son transport du trajet depuis elle, et
   * elle s'entend à l'instant. Le lead se prend tout seul — le premier qui
   * annonce le prend s'il est vacant — et se libère quand son détenteur cesse
   * d'annoncer. Une séance marche donc sans qu'on clique.
   */
  lead: string;
  /** Vrai quand le leader, c'est nous. */
  isLeader: boolean;
  /**
   * Demande au service de nous confier le lead, ou de le rendre. Faux s'il
   * refuse.
   *
   * Un geste sur soi : l'identité vient du jeton, personne ne désigne
   * quelqu'un d'autre. Prendre le lead éteint le jam ; c'est le service qui
   * tient l'exclusion. Même discipline que `setJam` : rien d'appliqué
   * localement, l'état revient par la métadonnée.
   */
  setLead(take: boolean): Promise<boolean>;
  /**
   * Les lignes de mesure du rouleau sont-elles masquées, pour toute la séance.
   * Lu dans l'état publié.
   *
   * Un réglage d'écran, pas un choix personnel : devant un public, la trame du
   * rouleau fait partie de ce qu'on montre, et c'est l'interprète qui en
   * décide. Un spectateur arrivé en cours de récital le lit dans la métadonnée,
   * comme le reste de la séance.
   */
  measuresHidden: boolean;
  /**
   * Demande au service de masquer les lignes de mesure, ou de les rétablir.
   * Faux s'il refuse.
   *
   * Même porte que la pause et le jam : le service applique, publie, et
   * l'interrupteur reflète la métadonnée. Rien n'est appliqué localement en
   * attendant la réponse. Un spectateur ne décide pas de ce que la salle voit,
   * et le service le refuserait de toute façon.
   */
  setMeasuresHidden(hidden: boolean): Promise<boolean>;
  /**
   * Le cadrage de chaque caméra de la séance, par identité LiveKit de qui la
   * publie. Vide tant que personne n'a rien cadré.
   */
  cameras: CameraFramings;
  /**
   * Pose le cadrage d'une caméra, ou le retire — un cadrage neutre sort de la
   * table. Faux si le service refuse.
   *
   * # Ce que ce geste fait vraiment
   *
   * Il ne change rien à l'écran de qui le fait. Il publie un réglage que
   * **l'appareil qui filme** applique à sa propre capture, avant son encodeur :
   * les pixels écartés ne sortent pas de l'appareil, donc ni vers la salle, ni
   * vers l'enregistrement, ni vers les fichiers du replay. C'est la seule façon
   * qu'un recadrage masque vraiment quelque chose. Voir `CameraFraming`.
   *
   * Entre le geste et son application sur un téléphone, il s'écoule un
   * aller-retour et une republication de métadonnée : l'appareil filme encore
   * large pendant ce temps. Le rideau couvre cette fenêtre.
   *
   * La table part **entière** : c'est ainsi que le service la lit, et c'est ce
   * qui rend le retrait d'une caméra impossible à oublier.
   */
  setCameraFraming(participant: string, framing: CameraFraming): Promise<boolean>;
  /**
   * Demande une qualité pour une source vidéo reçue ; `null` rend la valeur par
   * défaut.
   *
   * C'est ce qui montre l'image qui décide, parce que c'est la seule chose qui
   * connaisse sa taille : une vignette de coin se contente de 640×360, la même
   * image en grand demande la couche haute.
   */
  watchVideo(sourceId: string, quality: VideoQuality | null): void;
  /**
   * Lit ce que les caméras de la séance ont déclaré d'elles-mêmes : leurs
   * objectifs, ce qu'elles captent, l'étendue de leur zoom.
   *
   * Hors de la métadonnée de salle, qui part à tout le monde à chaque
   * changement : ces valeurs n'intéressent que qui cadre, et seulement pendant
   * qu'il cadre. Le service les réserve d'ailleurs à qui pilote.
   *
   * Une table vide quand rien ne répond — un service redémarré, un appareil
   * qui n'a pas pu se déclarer. Le studio cadre alors sans dimensions connues,
   * comme avant que la route existe.
   */
  fetchDeclaredCameras(): Promise<DeclaredCameras>;
  /**
   * Le rideau est baissé : plus rien de cette scène ne sort — ni notes, ni
   * caméras.
   *
   * Le temps, lui, continue de couler : ce n'est pas la pause. L'interprète
   * joue, voit son rouleau avancer, cadre une deuxième caméra ; la salle
   * n'en reçoit rien et l'apprend d'un mot.
   */
  curtain: boolean;
  /**
   * Baisse le rideau, ou le relève. Faux si le service refuse.
   *
   * Même porte que la trame et la pause : le geste va au service, qui publie,
   * et tout le monde s'y range — l'émetteur pour se taire, le récepteur pour
   * écarter ce qui serait encore en vol, un téléphone appairé pour sortir sa
   * caméra de la salle. Rien n'est appliqué localement en attendant : un
   * rideau qu'on croirait baissé sans qu'il le soit serait le pire des
   * mensonges de cette interface.
   */
  setCurtain(down: boolean): Promise<boolean>;
  /**
   * Le chat est fermé : la salle ne peut plus rien y dire, l'interprète si.
   * Un fait de séance, lu dans l'état publié comme le rideau : celui qui
   * arrive après le geste le trouve fermé comme les autres.
   */
  chatClosed: boolean;
  /**
   * Ferme le chat à la salle, ou le rouvre. Faux si le service refuse. Même
   * porte que le rideau : rien n'est appliqué localement en attendant.
   */
  setChatClosed(closed: boolean): Promise<boolean>;
  /**
   * La prise en cours : 1 dès l'ouverture, puis 2, 3… Lue dans l'état publié.
   *
   * Quelqu'un qui s'enregistre seul reprend la chanson du début jusqu'à la
   * version qu'il gardera ; la prise dit laquelle il joue en ce moment. Affichée
   * pendant qu'on joue, elle rend le découpage lisible au lieu d'être une
   * devinette qu'on ferait à la relecture.
   *
   * Vaut 1 hors séance et tant que rien n'est publié : on est toujours dans une
   * prise, même la première.
   */
  take: number;
  /**
   * Demande au service d'ouvrir la prise suivante, ici et maintenant dans la
   * musique. Faux s'il refuse.
   *
   * Interprète seulement : le service refuse la prise de quiconque ne mène pas
   * la séance, parce qu'on ne découpe pas l'enregistrement d'un autre. Même
   * discipline que `setJam` : rien n'est appliqué localement, le compteur revient
   * par la métadonnée. Un envoi rejoué — clic double, réessai — n'ouvre pas deux
   * prises : voir `sendNextTake`.
   */
  nextTake(): Promise<boolean>;
  /**
   * Ce poste a-t-il le droit d'émettre : en jam, ou quand il mène.
   *
   * C'est ce qui décide si le clavier capte au présent vrai ou à sa tête de
   * lecture, et ce que le panneau peut dire de ce que les autres entendent —
   * un auditeur n'est entendu de personne.
   */
  emitting: boolean;
  /**
   * Le délai propre de **ce** poste, en millisecondes : le délai commun publié
   * quand le jam tourne, sinon ce que le service a répondu à notre dernière
   * annonce partie. Zéro pour le leader, son trajet depuis le leader pour un
   * auditeur, le même pour tous en jam. C'est le nombre posé sur le
   * transport ; le panneau le montre à côté de ce que le transport tient, pour
   * voir si l'un a bien suivi l'autre. Chez un spectateur de récital, le
   * transport tient en plus son horizon : voir `transportDelayMs`.
   */
  delayMs: number;
  /**
   * Les grandeurs de l'alignement, telles qu'elles sont.
   *
   * Exposées pour être **lues à l'écran**, sur deux machines à la fois : quand
   * deux transports divergent, comparer ces quatre nombres répond en une
   * capture là où le raisonnement s'égare. L'écart entre deux positions ne se
   * déduit d'aucun journal local.
   */
  sessionState: LiveSessionState | null;
  clockOffsetMs: number | null;
  /**
   * De combien l'écart d'horloge tenu s'est déplacé depuis le premier relevé,
   * en millisecondes. C'est la dérive entre le quartz de cette machine et
   * l'horloge du service, corrigée au fil des relevés : sa pente, lue au bout
   * d'une heure, dit de combien les deux diffèrent. `null` hors séance.
   */
  clockDriftMs: number | null;
  /**
   * Ce que *cette* machine ajoute avant de sonner une note, en deux moitiés :
   * l'ordonnancement audio, qui est un réglage de l'application, et le tampon
   * de sortie, qui appartient au système. C'est le terme que le pair d'en face
   * subit quand on joue — et, symétriquement, celui qu'on subit quand il joue.
   */
  localLatency: LocalAudioLatency;
  /** Message affichable après un échec, `null` sinon. */
  error: string | null;
  /**
   * Message affichable quand la mise en marche n'a pas abouti, `null` sinon.
   *
   * Distinct d'`error`, qui parle de la *connexion* : ici la salle est jointe,
   * les notes circulent, mais la grille de temps reste figée. Deux pannes
   * différentes, deux messages — les confondre enverrait l'utilisateur relancer
   * une connexion qui marche.
   */
  startFailure: string | null;
  /**
   * L'enregistrement du récital, pour la marque « enregistré » de l'interprète.
   * `null` hors récital et chez un spectateur, qui n'a rien à en faire. Voir
   * `LiveRecitalRecording` : `unavailable` dit que l'enregistreur n'a pas
   * accepté de commencer.
   */
  recording: LiveRecitalRecording | null;
  /**
   * Arrête l'enregistrement du récital. Interprète seulement, pendant le
   * récital ; rien ne le relance ensuite. Faux si l'API refuse.
   */
  stopRecording(): Promise<boolean>;
  /**
   * Les portes du récital sont-elles ouvertes ? `null` hors récital. Faux
   * pendant le sas : l'interprète est seul sur scène, à brancher sa caméra.
   */
  doorsOpen: boolean | null;
  /**
   * Ouvre au public : le récital commence, et l'enregistrement avec lui.
   * Interprète seulement. Faux si l'API refuse.
   */
  openDoors(): Promise<boolean>;
  /**
   * Demande au service de figer ou de relancer la séance. Faux s'il refuse.
   *
   * C'est la seule porte : le service est l'unique autorité sur la position
   * partagée, et un client qui basculerait sa propre session verrait la
   * métadonnée la remettre où elle était.
   */
  setTransportPaused(paused: boolean): Promise<boolean>;
  /**
   * « Inviter » : ouvre une séance pour jouer à plusieurs, et y entre sans
   * quitter le studio. La séance naît en mode Jam, avec un code et un lien.
   *
   * Faux quand rien ne s'est ouvert : sans compte, l'écran de compte est déjà
   * à l'écran ; sur un refus, le motif est dans `error`.
   */
  invite(): Promise<boolean>;
  /**
   * « Rejoindre » : entre dans la salle qu'un code a désignée, sans quitter le
   * studio. Le même chemin qu'un lien reçu — l'adresse prend la salle, et la
   * jointure part de là, compte compris.
   */
  enter(room: string): void;
  /** Quitte la session en cours. Sans effet hors session. */
  leave(): Promise<void>;
  /** Recopie le lien dans le presse-papier. Faux si le navigateur refuse. */
  copyShareUrl(): Promise<boolean>;
}

/** Le silence du curseur de tempo après lequel le dernier tempo part. Voir `proposeTempo`. */
const TEMPO_PROPOSAL_QUIET_MS = 250;

/** La grille du studio hors séance : ce que la séance remplace, et rend en partant. */
interface SoloGrid {
  timeline: MusicalTimeline;
  originalBpm: number;
  bpm: number;
  signature: Signature;
}

function soloGridOf(studio: Studio): SoloGrid {
  return {
    timeline: studio.getTransport().timeline,
    originalBpm: studio.originalBpm,
    bpm: studio.bpm.value,
    signature: studio.continuousMetronome.signature.value,
  };
}

/**
 * Ce que le serveur rend de la latence locale : rien, parce qu'il n'a ni
 * contexte audio ni machine. C'est aussi ce que rend le premier rendu client,
 * qui doit dire la même chose que le HTML qu'il hydrate.
 */
const SERVER_LATENCY: LocalAudioLatency = { lookAheadMs: 0, outputMs: 0 };

const LiveSessionContext = createContext<LiveSessionContextValue | null>(null);

/**
 * La table de cadrage d'une séance qui n'en a pas.
 *
 * Une constante et non un littéral dans le rendu : c'est une valeur de
 * contexte, et un objet neuf à chaque rendu ferait refaire à tout ce qui la lit
 * un travail qui n'a pas changé.
 */
const EMPTY_FRAMINGS: CameraFramings = {};

export function LiveSessionProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  /**
   * Le compte, et l'écran qui le demande.
   *
   * La règle vit **ici** : rejoindre est un paramètre d'URL qu'aucun bouton ne
   * voit passer, et c'est ici qu'il se lit.
   */
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { openAccountModal } = useAccountModal();

  /**
   * L'état de départ se lit dans l'URL, il ne s'y écrit pas.
   *
   * Arriver sur un lien de live, c'est arriver *en train de se connecter* : la
   * jointure automatique plus bas est la conséquence de cet état, pas sa cause.
   * Le poser à « off » puis le corriger depuis l'effet ferait clignoter la
   * barre, et surtout ferait de l'effet une source d'état — ce qu'il n'est pas.
   */
  const [status, setStatus] = useState<LiveStatus>(() =>
    liveRoomFromParams(searchParams) === null && recitalIdFromParams(searchParams) === null ? "off" : "opening",
  );
  const [room, setRoom] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  /*
    En état et non dans la seule `ticketRef` : le bandeau doit se repeindre
    quand le code arrive, et une ref ne déclenche aucun rendu. La ref, elle,
    reste la source des appels au service, qui se font hors rendu.
  */
  const [session, setSession] = useState<LiveSessionSummary | null>(null);
  const [participants, setParticipants] = useState<readonly LiveParticipant[]>([]);
  const [identity, setIdentity] = useState<string | null>(null);
  const [role, setRole] = useState<LiveRole | null>(null);
  const isListener = role === "listener";

  /*
    La vidéo de la séance, lue dans son moteur (`LiveSession.video`) et mise en
    état pour repeindre. Les gestes passent par le moteur, qui tient la dernière
    valeur ; les règles du récital sont dans `recital-video.ts`.
  */
  const [videoSources, setVideoSources] = useState<readonly VideoSource[]>([]);
  const [videoSelf, setVideoSelf] = useState<string | null>(null);
  const [localCamera, setLocalCamera] = useState<LocalCamera | null>(null);
  const [phonePairing, setPhonePairing] = useState<VideoPairingLink | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  /**
   * L'écart mesuré contre le service, et le dernier état qu'il a publié.
   *
   * En état React et non en `ref` : les deux arrivent séparément — la mesure
   * après un aller-retour, l'état à la jointure puis à chaque changement — et
   * l'origine ne peut être posée qu'une fois les deux connus. Un effet qui les
   * observe exprime cette attente mieux qu'un appel recopié aux deux endroits.
   */
  const [clockOffsetMs, setClockOffsetMs] = useState<number | null>(null);
  const [sessionState, setSessionState] = useState<LiveSessionState | null>(null);
  const [clockDriftMs, setClockDriftMs] = useState<number | null>(null);

  /**
   * L'écart tenu, relevé après relevé, asservi. Une instance pour la vie du
   * fournisseur, remise à zéro à chaque démontage de séance.
   */
  const [aligner] = useState(() => new ClockAligner());

  /**
   * Le délai privé : ce que le service a répondu à notre dernière annonce
   * partie. Voir `PrivateDelay` pour ce qui fait qu'une réponse compte.
   *
   * L'instance vit autant que le fournisseur, comme `aligner`, et se remet à
   * zéro à chaque démontage de séance : les réponses encore en vol d'une
   * séance quittée ne touchent pas la suivante. L'état React n'en est que la
   * copie qui fait repeindre.
   */
  const [privateDelay] = useState(() => new PrivateDelay());
  const [privateDelayMs, setPrivateDelayMs] = useState<number | null>(null);

  /**
   * Ce que le service dit de **nous** : le délai à poser sur le transport, et
   * le droit d'émettre.
   *
   * Aucune condition locale, aucun calcul : le délai propre est le délai
   * commun publié quand le jam tourne, la réponse à notre dernière annonce
   * sinon, tel quel (voir `ownDelayMs`). Le service sait quel régime tourne et
   * ce qu'il implique pour qui, et le nombre rendu est le nombre appliqué.
   * Hors séance, ni état ni réponse : zéro, et on n'émet pas.
   *
   * Un spectateur n'émet jamais, quoi que dise l'état publié : son jeton ne
   * lui laisse rien publier, et rien n'est branché chez lui pour essayer. Un
   * jam allumé par l'interprète le ferait pourtant passer pour émetteur — au
   * clavier, qui capterait en avance pour rien, et au panneau, qui dirait
   * « m'entend » d'un son qui n'existe pas.
   */
  const delayMs = ownDelayMs(sessionState, privateDelayMs);
  const emitting = !isListener && canEmit(sessionState, identity ?? "");
  const jam = sessionState?.jam ?? false;
  const measuresHidden = sessionState?.measuresHidden ?? false;
  const cameras = sessionState?.cameras ?? EMPTY_FRAMINGS;
  const curtain = sessionState?.curtain ?? false;
  const chatClosed = sessionState?.chatClosed ?? false;
  // Un défaut à 1 et non à zéro : on est toujours dans une prise, et un panneau
  // qui annoncerait « Prise 0 » le temps de la première métadonnée mentirait.
  const take = sessionState?.take ?? 1;
  const lead = sessionState?.lead ?? "";
  const isLeader = lead !== "" && lead === identity;
  const headcount = roomHeadcount(sessionState, participants.length);

  /**
   * L'écart mesuré, lisible hors du rendu.
   *
   * Double de l'état, et à dessein : `startSession` en a besoin dans une
   * continuation, où lire un état ne donnerait pas la valeur fraîche.
   */
  const clockOffsetRef = useRef<number | null>(null);

  /**
   * Le dernier état publié, lisible hors du rendu.
   *
   * Double de l'état React, et à dessein : la mise en marche le consulte dans
   * une continuation, où lire un état ne donnerait pas la valeur fraîche.
   */
  const sessionStateRef = useRef<LiveSessionState | null>(null);

  /** De quoi lancer la séance dès que l'écart *et* l'état sont connus. */
  const pendingStartRef = useRef<{ ticket: LiveSessionTicket; offsetMs: number } | null>(null);

  /**
   * Le billet de la séance en cours.
   *
   * Gardé au-delà de la jointure parce que piloter la séance en a besoin :
   * l'URL du service, la salle et le jeton qui prouve qu'on y a droit.
   */
  const ticketRef = useRef<LiveSessionTicket | null>(null);

  /**
   * La séance a déjà été vue en marche.
   *
   * C'est ce qui distingue « elle n'a jamais démarré » de « quelqu'un vient de
   * la mettre en pause ». Sans ce drapeau, la relance de la mise en marche
   * défairait la pause d'un participant, à chaque fois, chez tout le monde.
   */
  const startedOnceRef = useRef(false);

  /** Une mise en marche est en cours : les deux déclencheurs n'en font qu'une. */
  const startingRef = useRef(false);

  const [error, setError] = useState<string | null>(null);
  const [startFailure, setStartFailure] = useState<string | null>(null);

  const { studio } = useStudio();

  /**
   * Le studio, lisible depuis `teardown`, qui n'a pas de dépendances pour ne
   * pas se recréer à chaque rendu.
   */
  const studioRef = useRef(studio);

  useEffect(() => {
    studioRef.current = studio;
  }, [studio]);

  /**
   * Ce que cette machine ajoute avant qu'une note sonne.
   *
   * Relue à chaque appel plutôt que calculée une fois : `outputLatency` n'est
   * renseigné qu'une fois le contexte audio réellement démarré, donc après le
   * premier geste de l'utilisateur. La fenêtre d'anticipation, elle, ne bouge
   * plus — elle vaut zéro partout.
   */
  const readLocalLatency = useCallback((): LocalAudioLatency => {
    const outputSeconds = studio?.getOutputLatency() ?? 0;

    return {
      lookAheadMs: getToneLookAhead() * 1000,
      outputMs: outputSeconds * 1000,
    };
  }, [studio]);

  const sessionRef = useRef<LiveSession | null>(null);
  /**
   * Les désabonnements aux observables de la session. Collectés pour être
   * rendus dans `teardown` : la session est disposée juste après, mais entre
   * la déconnexion (asynchrone) et `dispose`, ses derniers états poseraient
   * encore des `setState` sur un fournisseur qui a déjà tout remis à zéro.
   */
  const sessionUnsubscribesRef = useRef<Array<() => void>>([]);
  // La salle qui parle, une par séance : créée à la connexion, libérée au
  // démontage. En ref pour la libérer, en état pour que les widgets la lisent.
  const hallRef = useRef<LiveHall | null>(null);
  const [hall, setHall] = useState<LiveHall | null>(null);
  const bridgeRef = useRef<LaneLiveBridge | null>(null);
  /** La piste des partenaires. Tenue pour retrouver la lane de chacun. */
  const peersRef = useRef<LiveControler | null>(null);

  /**
   * La session de studio de la séance.
   *
   * C'est elle qui porte la matière du live — la lane du piano qu'on diffuse,
   * la piste des partenaires — et son origine temporelle. `LiveControler` et
   * `LaneLiveBridge` ne sont plus greffés sur le studio : ce sont des pièces de
   * cette session, et elles disparaissent avec elle.
   */
  const sharedRef = useRef<StudioSession | null>(null);

  /** La session à qui rendre la scène en quittant la séance. */
  const previousSessionRef = useRef<StudioSession | null>(null);
  /**
   * Salle vers laquelle une connexion est engagée. Tenue dans une ref et non
   * dans l'état : elle est lue par l'effet de jointure automatique, qui ne doit
   * pas se redéclencher au rendu que provoquerait un `setState`.
   */
  const targetRoomRef = useRef<string | null>(null);

  /**
   * La salle qu'on a déjà entrepris de rejoindre.
   *
   * Posée **avant** l'appel réseau, et non depuis `connect` : c'est tout
   * l'intérêt. L'effet de jointure automatique dépend de `studio`, qui passe de
   * `null` à monté — il se rejouait donc pendant que le premier billet était
   * encore en vol, avec un garde-fou encore vide. Deux billets partaient, deux
   * connexions s'ouvraient, et LiveKit expulsait la première puisqu'elles
   * portaient la même identité.
   *
   * Distincte de `targetRoomRef`, que `teardown` remet à zéro : celle-ci ne
   * doit pas l'être, sans quoi un échec relancerait la jointure en boucle.
   * Seul `leave` l'efface, parce que c'est un geste.
   */
  const joinAttemptedRef = useRef<string | null>(null);

  /**
   * La salle pour laquelle on a déjà demandé un compte.
   *
   * L'écran de compte vit dans un autre fournisseur : l'ouvrir provoque un
   * rendu qui redescend jusqu'ici. Sans ce garde-fou, l'effet de jointure le
   * rouvrirait à chaque passage, et une personne qui vient de le fermer le
   * verrait revenir indéfiniment.
   */
  const authPromptedRef = useRef<string | null>(null);

  /** La remesure périodique de l'écart d'horloge. Voir `REALIGN_INTERVAL_MS`. */
  const realignRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * La retenue posée sur les couches de vélocité pendant la séance. Voir
   * `holdSamplerLayers` : rien ne se monte sur le fil principal pendant qu'on
   * joue à plusieurs.
   */
  const layerHoldRef = useRef<(() => void) | null>(null);

  /**
   * La ligne de séance telle qu'elle atteint notre tête de lecture : chaque
   * changement publié y prend effet un délai de restitution plus tard. Voir
   * `lib/live/delayed-line.ts`.
   */
  const delayedLineRef = useRef(new DelayedLine());

  /**
   * La grille, le tempo et le chiffrage du studio avant la séance, rendus au
   * démontage. La séance pose sa propre grille (`applySessionTempo`) ; un
   * morceau ouvert avant retrouve la sienne en sortant.
   *
   * Pris à l'entrée, avant que la séance ne pose rien : c'est aussi ce que
   * l'interprète propose à une séance neuve (voir `carryEntryGrid`).
   */
  const soloTempoRef = useRef<SoloGrid | null>(null);

  /** La grille déjà posée, pour ne la remplacer que quand elle change. */
  const tempoKeyRef = useRef<string | null>(null);

  /** Le tempo demandé au curseur et pas encore parti. Voir `proposeTempo`. */
  const tempoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const proposeTempoRef = useRef<(bpm: number) => void>(() => undefined);

  const teardown = useCallback(async () => {
    if (realignRef.current !== null) {
      clearInterval(realignRef.current);
      realignRef.current = null;
    }

    layerHoldRef.current?.();
    layerHoldRef.current = null;

    bridgeRef.current?.dispose();
    bridgeRef.current = null;
    peersRef.current = null;
    // Le rouleau est rendu au joueur, que la séance l'ait fermé ou non :
    // rouvrir ce qui est ouvert ne coûte rien, et un verrou qui survivrait à
    // la séance serait un studio figé sans que rien ne le dise.
    studioRef.current?.setNavigationLocked(false);

    // Le tempo revient à la vue, et la grille d'avant la séance au transport.
    if (tempoTimerRef.current !== null) {
      clearTimeout(tempoTimerRef.current);
      tempoTimerRef.current = null;
    }

    studioRef.current?.setTempoProposer(null);
    studioRef.current?.setSignatureProposer(null);

    const solo = soloTempoRef.current;
    soloTempoRef.current = null;
    tempoKeyRef.current = null;
    delayedLineRef.current.reset();

    if (solo !== null && studioRef.current !== null) {
      // Le chiffrage avant la grille : posé sur la grille de la séance, il ne
      // redessine rien ; la grille rendue est celle qu'il avait dessinée.
      studioRef.current.setSignature(solo.signature);
      studioRef.current.getTransport().setMusicalTimeline(solo.timeline);
      studioRef.current.setOriginalBpm(solo.originalBpm);
      studioRef.current.setBpm(solo.bpm);
    }

    setRole(null);
    // Le compteur de dépassement du tampon parle d'une séance ; le laisser
    // courir d'une séance à l'autre rendrait le panneau illisible. Les relevés
    // de trajet, eux, décriraient une liaison qui n'existe plus — et ils
    // *décident* du délai, donc les garder ferait démarrer la séance suivante
    // sur la gigue de la précédente.
    resetPlayoutOverruns();
    resetTransit();
    // Et avec l'état et la réponse de la dernière annonce, le délai de
    // restitution retombe à zéro : il n'a de sens qu'en séance.
    setSessionState(null);
    privateDelay.reset();
    setPrivateDelayMs(null);
    clockOffsetRef.current = null;
    sessionStateRef.current = null;
    pendingStartRef.current = null;
    ticketRef.current = null;
    setSession(null);
    hallRef.current?.dispose();
    hallRef.current = null;
    setHall(null);
    startedOnceRef.current = false;
    startingRef.current = false;
    setStartFailure(null);
    setClockOffsetMs(null);
    setClockDriftMs(null);
    aligner.reset();

    const shared = sharedRef.current;
    sharedRef.current = null;

    if (shared !== null) {
      const studioNow = studioRef.current;

      // La scène revient à la session solo, qui reprend là où elle en était.
      // Une session solo neuve, elle, adopte l'instant présent comme origine :
      // rien ne saute d'un cas à l'autre.
      studioNow?.activateSession(previousSessionRef.current ?? new StudioSession("Solo"));
      previousSessionRef.current = null;

      // Puis seulement la séance est oubliée : ses pistes, ses lanes, leurs
      // synthés partent avec elle.
      studioNow?.getSessions().forget(shared);
    }

    // La webcam s'éteint avec la séance — le moteur l'arrête à la déconnexion,
    // plus bas : un voyant de caméra allumé hors de toute salle ne filmerait
    // pour personne.
    setVideoSources([]);
    setVideoSelf(null);
    setLocalCamera(null);
    setPhonePairing(null);
    setCameraError(null);

    const session = sessionRef.current;
    sessionRef.current = null;
    registerLiveSession(null);
    targetRoomRef.current = null;

    for (const unsubscribe of sessionUnsubscribesRef.current) {
      unsubscribe();
    }

    sessionUnsubscribesRef.current = [];

    if (session !== null) {
      await session.disconnect().catch(() => undefined);
      session.dispose();
    }
  }, [aligner, privateDelay]);

  /**
   * Sortir de la séance : tout ce que quitter défait, sans le geste de quitter.
   *
   * `leave` passe par là après avoir fermé la salle, et la séance qui se ferme
   * sous nos pieds y passe aussi. Un seul chemin, parce qu'une sortie à moitié
   * faite laisse un studio *en séance sans séance* : les lanes des partenaires,
   * le délai de restitution, le rouleau verrouillé, la salle dans l'URL — tout
   * ce qu'une séance pose et que personne ne retirerait.
   *
   * `notice` est ce qu'il y a à dire de cette sortie, `null` quand on est parti
   * soi-même : on sait déjà pourquoi. `to` déroute la sortie ailleurs que sur
   * la page nue ; sans lui, l'adresse perd simplement sa salle.
   */
  const exit = useCallback(
    async (notice: string | null, to: string | null = null) => {
      await teardown();
      joinAttemptedRef.current = null;

      setStatus("off");
      setRoom(null);
      setIdentity(null);
      setShareUrl(null);
      setParticipants([]);
      setError(notice);

      // Sortir d'un récital oublie l'entrée : rouvrir son adresse repassera par
      // la salle d'attente.
      forgetRecitalEntry();

      const carriesRoom =
        liveRoomFromParams(searchParams) !== null || recitalIdFromParams(searchParams) !== null;
      const destination = to ?? (carriesRoom ? pathname : null);

      if (destination !== null) {
        router.replace(destination, { scroll: false });
      }
    },
    [pathname, router, searchParams, teardown],
  );

  /**
   * La salle a disparu sous nos pieds : l'interprète a arrêté son live, ou la
   * liaison est perdue pour de bon. Où cela mène chacun se lit dans
   * `exitOnDrop`.
   */
  const dropped = useCallback((evicted: boolean) => {
    const ticket = ticketRef.current;
    const recitalId = ticket?.session?.recital?.id ?? null;
    const { notice, to } = exitOnDrop(ticket?.role ?? null, recitalId);

    // Sorti par l'interprète : la salle d'attente le dira à la place de la
    // porte, que le service refermerait de toute façon. Voir `recital-entry.ts`.
    if (evicted && recitalId !== null) {
      rememberExclusion(recitalId);
    }

    // Qui reste sur place doit lire pourquoi : sorti d'une séance à plusieurs
    // que son hôte vient de terminer, rien d'autre ne le dirait — le mot ne
    // vivait que dans le bandeau, fermé avec la séance. La pastille de la
    // barre le montre, et sa croix l'efface.
    void exit(notice, to).then(() => {
      if (notice !== null && to === null) {
        setStatus("error");
      }
    });
  }, [exit]);

  /**
   * Les caméras de l'interprète : sa webcam, ses téléphones appairés.
   *
   * # Deux au plus
   *
   * Un récital en montre deux à la fois, quoi qu'elles filment. Une troisième
   * qui se met à filmer — un téléphone qui entre, la webcam qu'on allume — coupe
   * la plus ancienne : voir `onDeviceArrived` dans `connect`. Les règles vivent
   * dans `recital-video.ts` ; les gestes, dans le moteur vidéo de la séance, qui
   * ouvre la caméra, l'arrête, tient le lien d'appairage, et ne coupe un
   * appareil qu'une fois quand la règle et « Couper » le visent ensemble.
   */
  const removeCamera = useCallback(async (sourceId: string): Promise<boolean> => {
    const video = sessionRef.current?.video;

    if (video === undefined) {
      return false;
    }

    const ok = await video.remove(sourceId);

    setCameraError(ok ? null : "La caméra n'a pas pu être coupée. Réessaie.");

    return ok;
  }, []);

  const stopWebcam = useCallback(async (): Promise<void> => {
    await sessionRef.current?.video.stopCamera();
  }, []);

  const startWebcam = useCallback(
    async (deviceId: string | null = null): Promise<boolean> => {
      const session = sessionRef.current;

      if (session === null || ticketRef.current?.role === "listener") {
        return false;
      }

      const video = session.video;
      // Une webcam qui filmait déjà ne fait que changer d'image : elle garde son
      // rang, et la règle n'a rien à couper. L'appliquer quand même couperait à
      // tort une seconde caméra si un téléphone entre pendant le changement : sa
      // propre règle a déjà demandé d'éteindre la webcam, ce qui passe après.
      const alreadyFilming = video.camera.value !== null;

      try {
        await video.startCamera({ deviceId });
      } catch (error) {
        // La séance a pu partir pendant que le navigateur demandait la
        // permission : il n'y a plus rien à dire à personne.
        if (error instanceof VideoEngineClosedError || sessionRef.current !== session) {
          return false;
        }

        // Un refus du navigateur se dit en mots ; le reste, c'est la salle qui
        // n'a pas pris la piste.
        setCameraError(
          error instanceof DOMException || error instanceof TypeError
            ? describeCameraFailure(error)
            : "La webcam n'a pas pu partir dans le récital. Réessaie.",
        );

        return false;
      }

      setCameraError(null);

      if (!alreadyFilming) {
        cutExtraSources(
          {
            sources: video.sources.value,
            self: video.self,
            stopWebcam: () => video.stopCamera(),
            removeDevice: removeCamera,
          },
          video.sources.value.find((source) => source.local)?.id ?? null,
        );
      }

      return true;
    },
    [removeCamera],
  );

  const pairPhone = useCallback(async (): Promise<boolean> => {
    const video = sessionRef.current?.video;

    if (video === undefined || (ticketRef.current?.session?.recital ?? null) === null) {
      return false;
    }

    try {
      await video.pairDevice();
      setCameraError(null);

      return true;
    } catch (error) {
      if (!(error instanceof VideoEngineClosedError)) {
        setCameraError(error instanceof Error && error.message !== "" ? error.message : "Le lien n'a pas pu être préparé.");
      }

      return false;
    }
  }, []);

  const cancelPhonePairing = useCallback(() => {
    sessionRef.current?.video.cancelPairing();
  }, []);

  /**
   * Pose l'origine du transport pour que la séance tombe où le service la dit.
   *
   * # Il faut les deux, et c'est le piège
   *
   * L'écart d'horloge seul ne suffit pas, et le croire produit un résultat
   * spectaculaire : le service rend un horodatage Unix, notre horloge de
   * position compte depuis le chargement de la page, et l'écart entre les deux
   * se compte en milliards. Appliqué comme une origine, il envoie la tête de
   * lecture à des millions de mesures.
   *
   * L'écart sert à **traduire** l'instant serveur de l'état publié. C'est cette
   * traduction qui donne la position d'aujourd'hui, et c'est elle qu'on pose.
   *
   * # On la donne à la session, jamais au transport
   *
   * `StudioSession` est le seul objet qui écrive le temps du transport, et ce
   * n'est pas une convention de style : c'est ce qui permet de garer une
   * séance et d'y revenir. Un appelant qui poserait l'origine directement la
   * verrait effacée à la première bascule, sans que rien ne le signale.
   *
   * `setFixedOrigin` applique tout de suite quand la session est en scène, et
   * retient pour la prochaine activation sinon. Les deux besoins, une écriture.
   *
   * # C'est le seul chemin de la pause
   *
   * Une pause demandée ici (`setTransportPaused`) part au service et revient
   * par la métadonnée, exactement comme celle d'un partenaire : c'est cet
   * effet qui l'applique, dans les deux cas. Elle a aussi circulé de pair à
   * pair, par un pont qui rediffusait chaque bascule de la session de studio —
   * y compris celle que cet effet venait d'appliquer depuis la métadonnée.
   * Chaque client rediffusait donc la pause du service, et chacun adoptait la
   * position du dernier émetteur. Le pont est parti, avec sa trame.
   *
   * On n'applique pas non plus la pause localement en attendant la réponse du
   * service : ce serait rouvrir les deux autorités en miniature. L'aller-retour
   * coûte quelques dizaines de millisecondes sur un geste de pause, et rien
   * d'audible.
   *
   * # À notre tête de lecture
   *
   * Chaque changement de ligne — pause, reprise, déplacement — prend effet
   * chez nous un délai de restitution après avoir eu lieu, quand notre tête
   * l'atteint : c'est le même décalage que le jam, étendu à la ligne. Le
   * spectateur d'un récital entend donc la dernière note avant la pause, et
   * s'arrête dessus quatre secondes après l'interprète. Le leader, sans délai,
   * applique tout à l'instant. Voir `lib/live/delayed-line.ts`.
   *
   * # Et à chaque relevé d'horloge
   *
   * L'écart est remesuré en cadence et asservi ; chaque relevé le déplace d'un
   * millième et demi au plus, et l'origine suit. C'est une translation de la
   * grille, pas un déplacement de tête : aucun événement n'est émis, rien
   * n'est relâché ni reprogrammé, les notes déjà confiées au synthé gardent
   * leur instant et les suivantes prennent le nouveau. À cette taille, ça ne
   * s'entend pas ; recopier chaque relevé brut, si.
   */
  useEffect(() => {
    const shared = sharedRef.current;
    const transport = studioRef.current?.getTransport();

    if (sessionState === null || clockOffsetMs === null || shared === null) {
      return;
    }

    if (transport === undefined) {
      return;
    }

    const line = delayedLineRef.current;
    line.push({ positionMs: sessionState.positionMs, atMs: sessionState.atMs, paused: sessionState.paused });

    // Le délai que tient notre transport : le délai propre, plus l'horizon
    // d'un spectateur. C'est lui que la tête a de retard sur la séance.
    const delay = transportDelayMs(delayMs, role);
    let timer: ReturnType<typeof setTimeout> | null = null;

    const apply = () => {
      timer = null;

      const nowSeconds = transport.clock.currentTime;
      const reading = line.read(nowSeconds * 1000 + clockOffsetMs, delay);

      if (reading === null) {
        return;
      }

      // `elapsedTime()` doit valoir la position : l'origine est donc l'instant
      // local d'où cette position serait partie de zéro.
      const origin = nowSeconds - reading.positionMs / 1000;
      shared.setFixedOrigin(origin);

      // Et l'état de marche, qu'on ignorait — c'était la moitié manquante.
      //
      // Sans lui, une séance annoncée en pause voyait bien sa position lue,
      // mais son transport continuait de courir depuis l'origine qu'on venait
      // de poser. Deux machines entrées à trente secondes d'écart divergeaient
      // donc de trente secondes, définitivement.
      if (reading.paused) {
        shared.pause(reading.positionMs / 1000);
      } else {
        shared.resume(origin);
      }

      if (reading.nextChangeAtMs !== null) {
        const waitMs = reading.nextChangeAtMs - (transport.clock.currentTime * 1000 + clockOffsetMs);
        timer = setTimeout(apply, Math.max(0, waitMs));
      }
    };

    apply();

    return () => {
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [clockOffsetMs, sessionState, delayMs, role]);

  /**
   * Pose la grille de la séance sur le transport, et la remplace quand un
   * tempo change. Voir `lib/live/session-tempo.ts` : deux grilles différentes
   * faisaient tomber les notes des autres de plus en plus loin.
   */
  useEffect(() => {
    if (studio === null || sessionState === null || sharedRef.current === null) {
      return;
    }

    const { tempoBpm, tempoMarks, signature } = sessionState;
    const key = sessionTempoKey(tempoBpm, tempoMarks, signature);

    if (tempoKeyRef.current === key) {
      return;
    }

    if (soloTempoRef.current === null) {
      soloTempoRef.current = soloGridOf(studio);
    }

    tempoKeyRef.current = key;
    studio.applySessionTempo(
      buildSessionTimeline(tempoBpm, tempoMarks, SESSION_PPQ, signature),
      sessionBpm(tempoBpm, tempoMarks),
    );
  }, [sessionState, studio]);

  /**
   * Fait démarrer la séance, si et seulement si elle est encore en pause.
   *
   * # Pourquoi elle naît en pause
   *
   * Côté service, une séance neuve est figée, et c'est délibéré : si elle
   * naissait en marche, sa position avancerait dès sa création et le premier
   * arrivant trouverait une minute de musique que personne n'a jouée. La faire
   * démarrer est un geste de participant.
   *
   * # Pourquoi on n'envoie que la position publiée
   *
   * La reprise du service **n'est pas idempotente** : elle réécrit la position,
   * quelle qu'elle soit. Une version précédente envoyait ici
   * `transport.elapsedTime()` — pris avant que l'alignement ne soit appliqué,
   * donc valant le temps écoulé depuis l'activation. Un arrivant rembobinait
   * ainsi la séance de son propre retard, quelques secondes, pour tout le
   * monde.
   *
   * On n'envoie donc que ce que le service annonce déjà, et seulement quand il
   * annonce une pause. Une séance en marche n'est pas touchée : c'est
   * l'idempotence rendue par construction, sans rien attendre du service.
   *
   * # Pourquoi elle insiste
   *
   * Parce qu'elle est le seul chemin par lequel une séance peut se mettre en
   * marche : sans elle, la métadonnée reste figée et rien ne la changera. Un
   * envoi perdu immobilisait donc la séance définitivement, en silence — la
   * salle jointe, les notes qui circulent, et la grille de temps arrêtée. Voir
   * `START_RETRY_DELAYS_MS`.
   *
   * À chaque tentative on relit l'état publié : si quelqu'un d'autre a lancé la
   * séance entre-temps, il n'y a plus rien à faire, et sa position fait foi.
   */
  const startSession = useCallback(
    async (ticket: LiveSessionTicket, state: LiveSessionState | null, offsetMs: number) => {
      if (state === null || !state.paused || startedOnceRef.current) {
        return;
      }

      // Les deux déclencheurs — la mesure d'horloge et l'arrivée de l'état —
      // appellent tous les deux : le premier arrivé mène la tentative.
      if (startingRef.current) {
        return;
      }

      startingRef.current = true;
      setStartFailure(null);

      try {
        const solo = soloTempoRef.current;
        const clock = studioRef.current?.getTransport().clock;

        if (solo !== null && clock !== undefined) {
          await carryEntryGrid(ticket, sessionStateRef.current ?? state, solo, clock.currentTime * 1000 + offsetMs);
        }

        for (let attempt = 0; ; attempt += 1) {
          const published = sessionStateRef.current;

          // La séance a été lancée entre-temps, ou on a quitté la salle.
          // Par la salle et non par l'objet : un billet renouvelé après un
          // changement de nom reste la même entrée dans la même salle.
          if (published === null || !published.paused || ticketRef.current?.room !== ticket.room) {
            return;
          }

          const transport = studioRef.current?.getTransport();

          if (transport === undefined) {
            return;
          }

          const sent = await sendSessionCommand(
            ticket.clockUrl,
            ticket.room,
            ticket.token,
            "resume",
            published.positionMs,
            transport.clock.currentTime * 1000 + offsetMs,
          );

          if (sent) {
            return;
          }

          if (attempt >= START_RETRY_DELAYS_MS.length) {
            setStartFailure(START_FAILURE_MESSAGE);

            return;
          }

          await new Promise((resolve) => setTimeout(resolve, START_RETRY_DELAYS_MS[attempt]));
        }
      } finally {
        startingRef.current = false;
      }
    },
    [],
  );

  /**
   * Fige ou relance la séance, à la demande d'un participant.
   *
   * Le geste passe par le service et pas par la session de studio : lui seul
   * arbitre la position partagée, et basculer la nôtre ne ferait que se faire
   * corriger par la métadonnée suivante — c'est exactement ce qu'on observait
   * en croyant « ne pas pouvoir faire play ».
   *
   * La position envoyée est celle où la séance se trouve *maintenant*, traduite
   * de l'horloge du service : une reprise réécrit la position, donc lui donner
   * autre chose ferait sauter tout le monde.
   */
  const setTransportPaused = useCallback(async (paused: boolean): Promise<boolean> => {
    const ticket = ticketRef.current;
    const offsetMs = clockOffsetRef.current;
    const transport = studioRef.current?.getTransport();

    if (ticket === null || offsetMs === null || transport === undefined) {
      return false;
    }

    // Un spectateur ne pilote pas la séance : c'est l'interprète qui lance,
    // fige ou relance son récital, et le service refuserait de toute façon un
    // jeton sans droit de publier. Faux sans appel, pour que rien ne parte —
    // et que le refus ne s'affiche pas comme une panne de service.
    if (ticket.role === "listener") {
      return false;
    }

    const localNowMs = transport.clock.currentTime * 1000;
    const published = sessionStateRef.current;
    const positionMs = published === null ? 0 : positionNowMs(published, localNowMs, offsetMs);

    const sent = await sendSessionCommand(
      ticket.clockUrl,
      ticket.room,
      ticket.token,
      paused ? "pause" : "resume",
      positionMs,
      localNowMs + offsetMs,
    );

    if (!sent) {
      setStartFailure(START_FAILURE_MESSAGE);
    }

    return sent;
  }, []);

  /**
   * Propose un tempo à la séance, à la demande d'un participant qui la pilote.
   *
   * Le tick envoyé est celui du présent de la séance sur la grille en vigueur :
   * c'est le couple position, tick qui laisse tout le monde reconstruire la
   * même grille. Rien ne s'applique localement, comme pour la pause : la grille
   * revient par l'état publié. Seul le chiffre s'affiche tout de suite (voir
   * `Studio.setBpm`).
   *
   * Le curseur du tempo émet en rafale ; on n'envoie que le dernier tempo, un
   * quart de seconde après le dernier mouvement. Chaque envoi est une marque
   * dans l'état publié, et le service n'en garde qu'un nombre borné.
   */
  const proposeTempo = useCallback((bpm: number) => {
    if (tempoTimerRef.current !== null) {
      clearTimeout(tempoTimerRef.current);
    }

    tempoTimerRef.current = setTimeout(() => {
      tempoTimerRef.current = null;

      const ticket = ticketRef.current;
      const offsetMs = clockOffsetRef.current;
      const published = sessionStateRef.current;
      const transport = studioRef.current?.getTransport();

      if (ticket === null || offsetMs === null || published === null || transport === undefined) {
        return;
      }

      if (ticket.role === "listener") {
        return;
      }

      const localNowMs = transport.clock.currentTime * 1000;
      const positionMs = positionNowMs(published, localNowMs, offsetMs);
      const tick = transport.timeline.secondsToTick(positionMs / 1000) ?? 0;

      void sendSessionTempo(
        ticket.clockUrl,
        ticket.room,
        ticket.token,
        { bpm: proposedBpm(bpm), tick },
        positionMs,
        localNowMs + offsetMs,
      );
    }, TEMPO_PROPOSAL_QUIET_MS);
  }, []);

  proposeTempoRef.current = proposeTempo;

  /**
   * Propose un chiffrage à la séance : le choix du métronome, en séance.
   *
   * Comme le tempo, rien ne s'applique ici à la grille : elle revient par
   * l'état publié, et c'est ce qui la fait changer chez tout le monde au même
   * moment. Sans attente, contrairement au tempo : un chiffrage se choisit d'un
   * geste, il ne se glisse pas.
   */
  const proposeSignature = useCallback((signature: Signature) => {
    const ticket = ticketRef.current;
    const offsetMs = clockOffsetRef.current;
    const published = sessionStateRef.current;
    const transport = studioRef.current?.getTransport();

    if (ticket === null || offsetMs === null || published === null || transport === undefined) {
      return;
    }

    if (ticket.role === "listener") {
      return;
    }

    const localNowMs = transport.clock.currentTime * 1000;

    void sendSessionSignature(
      ticket.clockUrl,
      ticket.room,
      ticket.token,
      { beats: signature.num, unit: signature.den },
      positionNowMs(published, localNowMs, offsetMs),
      localNowMs + offsetMs,
    );
  }, []);

  /**
   * Allume ou éteint le mode Jam, à la demande d'un participant.
   *
   * Comme la pause : le geste va au service, seul à décider pour toute la
   * séance, et revient par la métadonnée. On n'applique rien localement en
   * attendant — ce serait rouvrir deux autorités en miniature — et l'échec ne
   * lève pas : le service peut être absent, la séance survit, seul le mode ne
   * change pas. Le motif est dans la console (cf. `sendJamMode`) ; c'est
   * l'interrupteur qui dit à l'écran que le geste n'a pas pris.
   */
  const setJam = useCallback(async (on: boolean): Promise<boolean> => {
    const ticket = ticketRef.current;

    // Un spectateur ne change pas le régime — même raison que pour la pause.
    if (ticket === null || ticket.role === "listener") {
      return false;
    }

    return sendJamMode(ticket.clockUrl, ticket.room, ticket.token, on);
  }, []);

  /**
   * Prend le lead, ou le rend, à la demande de ce participant.
   *
   * Même discipline que `setJam` : le geste va au service, seul à décider qui
   * mène — et à éteindre le jam si on prend le lead —, et revient par la
   * métadonnée. Rien n'est appliqué localement en attendant, et l'échec ne
   * lève pas : la séance survit, seul le lead ne change pas. Le motif est
   * dans la console (cf. `sendLead`) ; c'est le bouton qui dit à l'écran que
   * le geste n'a pas pris.
   */
  const setLead = useCallback(async (take: boolean): Promise<boolean> => {
    const ticket = ticketRef.current;

    // Un spectateur ne mène jamais : le service ne l'élit pas, et ne le
    // laisserait pas se désigner. Même raison que pour la pause.
    if (ticket === null || ticket.role === "listener") {
      return false;
    }

    return sendLead(ticket.clockUrl, ticket.room, ticket.token, take);
  }, []);

  /**
   * Masque les lignes de mesure, ou les rétablit, à la demande de l'interprète.
   *
   * Même discipline que `setJam` : le geste va au service, seul à décider pour
   * toute la séance, et revient par la métadonnée — c'est ce qui fait que le
   * public le voit, et qu'un spectateur arrivé après le geste le trouve déjà
   * posé. Rien n'est appliqué localement en attendant, et l'échec ne lève pas :
   * le motif est dans la console (cf. `sendSessionSettings`), et c'est
   * l'interrupteur qui dit à l'écran que le geste n'a pas pris.
   */
  const setMeasuresHidden = useCallback(async (hidden: boolean): Promise<boolean> => {
    const ticket = ticketRef.current;

    // Un spectateur ne règle pas ce que la salle regarde — même raison que
    // pour la pause, et le service refuserait.
    if (ticket === null || ticket.role === "listener") {
      return false;
    }

    return sendSessionSettings(ticket.clockUrl, ticket.room, ticket.token, { measuresHidden: hidden });
  }, []);

  /** Lit ce que les caméras de la séance ont déclaré. Voir le contrat. */
  const fetchCameras = useCallback(async (): Promise<DeclaredCameras> => {
    const ticket = ticketRef.current;

    if (ticket === null || ticket.role === "listener") {
      return {};
    }

    return fetchDeclaredCameras(ticket.clockUrl, ticket.room, ticket.token);
  }, []);

  /** Demande une qualité pour une source reçue. Voir `VideoEngine.watch`. */
  const watchVideo = useCallback((sourceId: string, quality: VideoQuality | null): void => {
    sessionRef.current?.video.watch(sourceId, quality);
  }, []);

  /**
   * Pose le cadrage d'une caméra, à la demande de l'interprète.
   *
   * La table publiée est relue **à l'instant du geste** (`sessionStateRef`) et
   * non capturée dans une dépendance : deux réglages coup sur coup — le zoom
   * d'une caméra, puis la rotation de l'autre — partent chacun d'un état à
   * jour, là où une table figée au rendu aurait effacé le premier avec le
   * second.
   */
  const setCameraFraming = useCallback(async (participant: string, framing: CameraFraming): Promise<boolean> => {
    const ticket = ticketRef.current;

    // Un spectateur ne cadre pas ce que la salle regarde, et le service le
    // refuserait.
    if (ticket === null || ticket.role === "listener" || participant === "") {
      return false;
    }

    const published = sessionStateRef.current?.cameras ?? EMPTY_FRAMINGS;
    const next: Record<string, CameraFraming> = { ...published };

    // Un cadrage neutre sort de la table plutôt que d'y rester à ne rien
    // demander : c'est aussi ce que fait « réinitialiser ».
    if (isDefaultFraming(framing)) {
      delete next[participant];
    } else {
      next[participant] = framing;
    }

    return sendSessionSettings(ticket.clockUrl, ticket.room, ticket.token, { cameras: next });
  }, []);

  /**
   * Baisse le rideau, ou le relève.
   *
   * Même chemin que la trame — c'est un réglage partagé, publié par le service
   * —, et la même garde : un spectateur ne décide pas de ce que la salle
   * reçoit, et le service le refuserait.
   */
  const setCurtain = useCallback(async (down: boolean): Promise<boolean> => {
    const ticket = ticketRef.current;

    if (ticket === null || ticket.role === "listener") {
      return false;
    }

    return sendSessionSettings(ticket.clockUrl, ticket.room, ticket.token, { curtain: down });
  }, []);

  const setChatClosed = useCallback(async (closed: boolean): Promise<boolean> => {
    const ticket = ticketRef.current;

    if (ticket === null || ticket.role === "listener") {
      return false;
    }

    return sendSessionSettings(ticket.clockUrl, ticket.room, ticket.token, { chatClosed: closed });
  }, []);

  /**
   * Ouvre la prise suivante, à la demande de l'interprète.
   *
   * # La position part avec, et c'est tout l'intérêt
   *
   * La frontière entre deux prises est un endroit de la **musique** — celui où
   * l'interprète vient de reprendre —, pas l'instant où le service recevra la
   * requête. On envoie donc la position de la séance *maintenant*, exactement
   * comme une pause, et le service la republie : le découpage survit à
   * l'amortissement des écritures, qui coûte plusieurs centaines de
   * millisecondes.
   *
   * # Ce qu'on annonce, c'est ce qu'on voit
   *
   * `fromTake` est lu dans le dernier état publié, pas dans une variable de
   * rendu : c'est la valeur que le service va comparer, et lui donner autre
   * chose ferait refuser le geste. Sans état publié, rien ne part — on ne sait
   * pas encore quelle prise tourne, et deviner en ouvrirait une de trop.
   *
   * Rien n'est appliqué localement : le compteur revient par la métadonnée,
   * comme la pause et le régime. L'échec ne lève pas, le motif est dans la
   * console (cf. `sendNextTake`).
   */
  const nextTake = useCallback(async (): Promise<boolean> => {
    const ticket = ticketRef.current;
    const offsetMs = clockOffsetRef.current;
    const published = sessionStateRef.current;
    const transport = studioRef.current?.getTransport();

    if (ticket === null || offsetMs === null || published === null || transport === undefined) {
      return false;
    }

    // Un spectateur ne découpe pas l'enregistrement de l'interprète — le
    // service le refuserait de toute façon, et le refus s'afficherait comme une
    // panne.
    if (ticket.role === "listener") {
      return false;
    }

    const localNowMs = transport.clock.currentTime * 1000;

    return sendNextTake(
      ticket.clockUrl,
      ticket.room,
      ticket.token,
      published.take,
      positionNowMs(published, localNowMs, offsetMs),
      localNowMs + offsetMs,
    );
  }, []);

  /**
   * Qui est dans la séance, demandé au service avec le billet de la séance en
   * cours.
   *
   * Une fonction plutôt qu'une liste tenue ici : la liste change toutes les
   * trois secondes pendant qu'on la regarde, et une valeur de contexte qui
   * change re-rend tous ses consommateurs — le studio entier — pour un
   * panneau. C'est le panneau qui la relève et la garde (voir
   * `useLiveParticipants`). Stable, pour que son relevé ne se relance pas à
   * chaque rendu.
   */
  const fetchParticipants = useCallback(async (): Promise<LiveParticipantsReply | null> => {
    const ticket = ticketRef.current;

    if (ticket === null) {
      return null;
    }

    return fetchLiveParticipants(ticket.clockUrl, ticket.room, ticket.token);
  }, []);

  /**
   * Mesure l'écart avec l'horloge de la séance.
   *
   * On mesure contre le service plutôt que contre un pair : le trajet n'est
   * alors qu'une seule jambe, et les erreurs ne s'enchaînent pas — chacun est à
   * une mesure de la même référence, au lieu de s'aligner sur quelqu'un qui
   * s'est aligné sur quelqu'un.
   *
   * Sans service joignable, on ne pose rien : les notes circulent quand même,
   * seul leur alignement se dégrade. Jouer désaligné vaut mieux que ne pas
   * jouer — et le relevé suivant, un quart de minute plus tard, retentera.
   *
   * Appelée à la jointure puis en cadence (`REALIGN_INTERVAL_MS`) : un écart
   * mesuré une fois dérive ensuite avec le quartz de la machine, et cette
   * dérive se confond avec le trajet dans ce que mesure la piste des
   * partenaires. Voir `ClockAligner` pour ce que chaque relevé peut déplacer.
   */
  const alignClock = useCallback(
    async (clockUrl: string): Promise<number | null> => {
      const measured = await measureClockOffset(clockUrl);

      if (measured === null) {
        return null;
      }

      // Asservi, pas recopié : le premier relevé est adopté, les suivants
      // corrigent d'un pas au plus. Voir `ClockAligner`.
      const held = aligner.accept(measured);

      clockOffsetRef.current = held;
      setClockOffsetMs(held);
      setClockDriftMs(aligner.driftMs);

      return held;
    },
    [aligner],
  );

  const connect = useCallback(
    async (ticket: LiveSessionTicket) => {
      await teardown();

      targetRoomRef.current = ticket.room;
      ticketRef.current = ticket;
      // Ce que l'API sait de la salle, quand elle en sait quelque chose : le
      // récital qu'elle porte, sa visibilité, et si elle est à nous.
      setSession(ticket.session ?? null);

      const session = new LiveSession({
        transport: new LiveKitTransport(),
        // La vidéo de la séance : couper un téléphone passe par le service, avec
        // le billet en vigueur ; en appairer un, par le récital que la salle
        // porte. Une séance sans récital ne propose pas d'appairer.
        videoControl: {
          removeDevice: (participant) => {
            const current = ticketRef.current ?? ticket;

            return removeLiveCamera(current.clockUrl, current.room, current.token, participant);
          },
        },
        videoPairing: ticket.session?.recital ? recitalPairing(ticket.session.recital.id) : undefined,
        // Le son sort par le contexte audio du studio, avec le tampon que le
        // studio retranche de ses notes : une voix et une note prises ensemble
        // sonnent ensemble. Aucun contexte n'écoute encore — un récital ne porte
        // pas de son — : le moteur ne s'abonne à rien.
        audioOutput: studio
          ? new WebAudioOutput({
              context: () => studio.getAudioContext(),
              latencyMs: () => studio.getOutputLatency() * 1000,
            })
          : undefined,
        // La parole — une voix, un visage filmé — tombe avec les notes, comme le
        // jeu : un récital et un jam calent tout sur elles. Un cours la fera
        // passer au plus tôt. Voir `lib/live/media-timing.ts`.
        speechTiming: "notes",
        // Son identité, pour savoir si elle mène : c'est elle qui garde
        // l'émission, et celle que le service publie pour le leader.
        identity: ticket.identity,
        // Le battement qui tient les radios éveillées, à la cadence que la
        // mesure a validée ou celle réglée depuis la console. Il ne part que
        // tant que le jam tourne, décision du 2026-09-14 : c'est le seul régime
        // où la gigue de chacun fait le délai de toute la salle. Voir
        // `LiveSession` et `radio-keepalive.ts`. Un spectateur n'en a jamais
        // besoin : il ne joue pour personne.
        keepaliveMs: ticket.role === "listener" ? 0 : radioKeepaliveMs(),
        // L'horloge du service, pour dater les trames et mesurer leur trajet.
        // Lue dans la ref, pas dans l'état : la session l'appelle hors du
        // rendu, à chaque geste et à chaque trame reçue. `null` tant que
        // l'écart n'est pas mesuré — les trames partent alors sans date, et
        // rien n'est mesuré à l'arrivée.
        serverNow: (positionSeconds) => {
          const offset = clockOffsetRef.current;

          if (offset === null) {
            return null;
          }

          const localMs = positionSeconds === undefined ? performance.now() : positionSeconds * 1000;

          return localMs + offset;
        },
        localLatency: readLocalLatency,
        // La session mesure et lisse, le contexte poste : c'est lui qui tient
        // le billet, et `lib/live/` ne doit connaître ni HTTP ni le service.
        // `announcePlayout` ne lève jamais, et une annonce perdue est
        // remplacée par la suivante au tour d'après.
        //
        // La réponse porte notre délai. Elle n'est retenue que si elle répond
        // à la dernière annonce partie — le numéro se prend au départ, pas à
        // l'arrivée : voir `PrivateDelay`. Une séance démontée entre-temps a
        // remis le compte à zéro, et sa réponse ne pose plus rien.
        //
        // Un spectateur annonce aussi : le service lui doit un délai, et il
        // ne le calcule que sur sa jambe. L'annonce ne lui vaut pas le lead —
        // le service n'élit que ceux dont le jeton peut publier.
        announcePeerInfo: (info) => {
          const departure = privateDelay.depart();

          // Le billet en vigueur, pas celui de la connexion : il se renouvelle
          // quand le nom du compte change, et c'est de son jeton que le service
          // tire le nom affiché dans la liste des participants.
          const current = ticketRef.current ?? ticket;

          void announcePlayout(current.clockUrl, current.room, current.token, info).then((reply) => {
            if (privateDelay.settle(departure, reply.delayMs)) {
              setPrivateDelayMs(privateDelay.delayMs);
            }
          });
        },
      });
      sessionRef.current = session;
      registerLiveSession(session);

      // La salle qui parle. Elle poste par le billet en vigueur — comme
      // l'annonce, il se renouvelle quand le nom du compte change — et lit ce
      // que le service publie sur le canal de données. Un spectateur y a les
      // mêmes droits que l'interprète : c'est le service qui parle à sa place.
      //
      // Les gestes de modération partent par les mêmes liens : c'est le
      // service qui les refuse à qui ne pilote pas, et le widget ne les
      // propose qu'à l'interprète.
      const nextHall = new LiveHall(
        {
          say: (text) => {
            const current = ticketRef.current ?? ticket;

            return sendChatMessage(current.clockUrl, current.room, current.token, text);
          },
          react: (count) => {
            const current = ticketRef.current ?? ticket;

            return sendReactions(current.clockUrl, current.room, current.token, count);
          },
          retract: (id) => {
            const current = ticketRef.current ?? ticket;

            return retractChatMessage(current.clockUrl, current.room, current.token, id);
          },
          mute: (who, muted) => {
            const current = ticketRef.current ?? ticket;

            return muteParticipant(current.clockUrl, current.room, current.token, who, muted);
          },
          exclude: (who) => {
            const current = ticketRef.current ?? ticket;

            return excludeParticipant(current.clockUrl, current.room, current.token, who);
          },
        },
        { identity: ticket.identity },
      );
      hallRef.current = nextHall;
      setHall(nextHall);

      const unsubscribes = sessionUnsubscribesRef.current;
      unsubscribes.push(session.onServerData((topic, payload) => nextHall.receive(topic, payload)));
      unsubscribes.push(session.state.subscribe((state) => {
        setStatus(toStatus(state));

        // La salle n'est plus là : l'interprète a arrêté, ou la liaison est
        // perdue pour de bon. Voir `dropped`. Notre propre départ ne passe pas
        // par là — `teardown` se désabonne avant de couper.
        if (state === "closed") {
          dropped(session.evicted);
        }
      }));
      unsubscribes.push(session.participants.subscribe((next) => setParticipants(next)));
      unsubscribes.push(session.video.sources.subscribe((next) => {
        setVideoSources(next);
        setVideoSelf(session.video.self);
      }));
      unsubscribes.push(session.video.camera.subscribe((next) => setLocalCamera(next)));
      unsubscribes.push(session.video.pairing.subscribe((next) => setPhonePairing(next)));

      // Chez l'interprète, un téléphone qui se met à filmer s'ajoute aux caméras
      // de ce poste. S'il en fait une troisième, la plus ancienne est coupée :
      // la webcam s'éteint, ou un téléphone sort de la salle. Le lien, lui, a
      // servi : le moteur l'a déjà retiré.
      if (ticket.role !== "listener") {
        unsubscribes.push(session.video.onDeviceArrived((arrived) => {
          cutExtraSources(
            {
              sources: session.video.sources.value,
              self: session.video.self,
              stopWebcam: () => session.video.stopCamera(),
              removeDevice: removeCamera,
            },
            arrived.id,
          );
        }));
      }

      // L'état arrive à la jointure puis à chaque changement, livré par
      // LiveKit. Il ne se pose que si l'écart d'horloge a été mesuré — sans
      // quoi son instant serveur ne veut rien dire chez nous.
      unsubscribes.push(session.sessionState.subscribe((state) => {
        sessionStateRef.current = state;
        setSessionState(state);

        // Une fois la séance vue en marche, la mise en marche automatique n'a
        // plus lieu d'être : ce qui suit est le transport d'un participant, et
        // relancer défairait sa pause.
        if (state !== null && !state.paused) {
          startedOnceRef.current = true;
          setStartFailure(null);
        }

        // L'état peut arriver **après** la mesure d'horloge : la métadonnée se
        // relit en insistant, l'alignement tient en quelques dizaines de
        // millisecondes. La mise en marche attend donc les deux, et part dès
        // que le second est là — sans quoi une séance en pause resterait figée
        // parce que personne ne l'aurait vue en pause.
        const pending = pendingStartRef.current;

        if (pending !== null) {
          void startSession(pending.ticket, state, pending.offsetMs);
        }
      }));

      // Rien ne monte de couche d'échantillons pendant la séance : ce travail
      // tomberait sur le fil principal pendant qu'on joue à plusieurs, et il
      // retarderait les notes dans les deux sens. Levée au démontage.
      layerHoldRef.current = holdSamplerLayers();

      if (studio) {
        // La séance est une session de studio à elle seule. Elle entre en
        // scène, et c'est en entrant qu'elle se dote d'une lane de piano — d'où
        // la certitude d'en avoir une ici, là où il fallait auparavant aller la
        // chercher à tâtons dans la piste du studio, avec un cas dégradé « pas
        // de lane, pas de diffusion » qui était un aveu.
        previousSessionRef.current = studio.getSession();

        const shared = new StudioSession("Séance");

        // Les deux seuls réglages qui font d'une session de studio une séance
        // partagée. Le studio n'en sait pas davantage : l'origine sera recalée
        // dès que l'écart d'horloges avec les pairs sera mesuré, et perdre le
        // son ne doit rien arrêter puisque les autres continuent de jouer.
        shared.setFixedOrigin(studio.getTransport().clock.currentTime);
        shared.setPausesOnAudioLoss(false);
        sharedRef.current = shared;
        studio.activateSession(shared);

        // La grille d'avant, prise avant que la séance ne pose la sienne : on
        // la rend en partant, et l'interprète la propose à une séance neuve.
        if (soloTempoRef.current === null) {
          soloTempoRef.current = soloGridOf(studio);
        }

        // Le tempo et le chiffrage deviennent ceux de la séance, proposés au
        // service par qui la pilote. Un spectateur n'a rien à proposer.
        if (ticket.role !== "listener") {
          studio.setTempoProposer((bpm) => proposeTempoRef.current(bpm));
          studio.setSignatureProposer(proposeSignature);
        }

        if (ticket.role !== "listener") {
          // Qui joue joue au direct — l'interprète d'un récital comme chacun
          // dans une séance à plusieurs (décidé le 2026-09-23) : le rouleau se
          // ferme chez lui — molette, glissé, minimap, tout ce qui passe par
          // `ViewNavigator`. Une vue écartée ferait jouer par-dessus un passage
          // qui n'est pas celui des autres. Le spectateur, lui, se promène : il
          // peut revenir au début, et « Revenir au live » le ramène. Rouvert
          // dans `teardown`.
          studio.setNavigationLocked(true);
        }

        if (ticket.role !== "listener") {
          // Le pont prend les gestes sur la lane du piano, pas sur le piano :
          // c'est elle qui sait à quel tick du transport partagé ils ont eu
          // lieu.
          //
          // Un spectateur n'en a pas : son jeton ne lui laisse rien publier,
          // et LiveKit jetterait ses trames. Ne pas le brancher vaut mieux
          // qu'un pont muet — rien ne s'émet, rien n'est retenu pour les trames
          // d'état, et le piano local reste ce qu'il est : un piano.
          bridgeRef.current = new LaneLiveBridge(shared.getPianoLane()!, session);
        }

        // Et le sens retour : une piste pour les partenaires, une lane chacun.
        // Pour le spectateur aussi — c'est ce qu'il regarde.
        const peers = new LiveControler(
          session,
          studio.getTransport(),
          studio.getAudioContext(),
          // Notre délai de restitution : un partant reste à l'écran le temps
          // que ses dernières notes tombent. Voir `LiveControler.forget`.
          () => studio.getLocalTransport().getPlayoutDelay(),
        );

        peersRef.current = peers;
        shared.addTrack(peers);
      }

      setRoom(ticket.room);
      setIdentity(ticket.identity);
      setRole(ticket.role);
      // Le lien d'un récital est sa page, pas la salle : c'est là qu'on
      // s'inscrit, qu'on voit « C'est parti », et qu'on fait le clic qui
      // réveille le son. Une séance ordinaire se partage par sa salle.
      const recital = ticket.session?.recital ?? null;
      setShareUrl(
        recital === null
          ? liveJoinUrl(window.location.origin, ticket.room)
          : `${window.location.origin}${recitalPath(recital.id)}`,
      );
      setStatus("connecting");

      await session.connect({ serverUrl: ticket.serverUrl, token: ticket.token });

      // L'ordre compte : l'écart d'abord, la mise en marche ensuite. Sans
      // l'écart, l'instant qu'on annoncerait au service n'aurait aucun sens sur
      // son horloge.
      //
      // Et l'écart se remesure ensuite en cadence, pour la vie de la séance :
      // c'est le premier relevé abouti qui arme la mise en marche, les
      // suivants ne font que corriger. Voir `alignClock`.
      const realign = async () => {
        if (sessionRef.current !== session) {
          return;
        }

        const offsetMs = await alignClock(ticket.clockUrl);

        if (offsetMs === null || sessionRef.current !== session) {
          return;
        }

        // La mise en marche n'est pas armée chez un spectateur : c'est
        // l'interprète qui lance son récital, et le service refuserait de
        // toute façon la reprise. L'écart, lui, est mesuré et remesuré comme
        // chez tout le monde — c'est lui qui place la position lue, et le
        // spectateur n'a que ça à regarder.
        if (ticket.role === "listener") {
          return;
        }

        if (pendingStartRef.current === null) {
          pendingStartRef.current = { ticket, offsetMs };
          void startSession(ticket, sessionStateRef.current, offsetMs);
        }
      };

      void realign();
      realignRef.current = setInterval(() => void realign(), REALIGN_INTERVAL_MS);
    },
    [alignClock, dropped, privateDelay, proposeSignature, readLocalLatency, removeCamera, startSession, studio, teardown],
  );

  /**
   * Applique **mon** délai : le transport recule, l'affichage suit.
   *
   * Le délai propre, tel quel, et rien d'autre : le service l'a calculé pour
   * moi — zéro si je mène, mon trajet depuis le leader si j'écoute, le pire de
   * la salle en jam. Un arrivant le pose dès la réponse à sa première annonce,
   * qui part à la connexion, au lieu de partir de zéro et d'y monter annonce
   * après annonce pendant que les notes du leader tombaient derrière sa tête
   * de lecture.
   *
   * Il devient le **délai de restitution** du transport local : la tête de
   * lecture court d'autant derrière la position de séance, et tout sonne
   * d'autant plus tard. C'est chez chaque récepteur que le tampon se prend :
   * un participant qui ne suit pas son délai entend de travers, mais ne casse
   * rien pour les autres.
   *
   * Rien à faire pour les lanes des partenaires : leurs notes portent un tick
   * vrai, et ma tête de lecture l'atteint `D` après leur geste. Tant que le
   * délai couvre le trajet, elles sont encore devant à l'arrivée et se
   * programment à leur tick.
   *
   * Le studio est une dépendance et non une ref : il arrive après le premier
   * rendu, et un délai déjà publié doit le rattraper quand il est là.
   */
  useEffect(() => {
    if (studio === null) {
      return;
    }

    // Une seule entrée : elle place la tête de lecture et, `D` au-dessus, la
    // ligne du présent que le roll dessine sur le même nombre.
    //
    // Le délai propre, plus l'horizon d'un spectateur de récital, pour qu'il
    // voie les notes de l'interprète tomber du haut. C'est la seule exception
    // au « tel quel » : un spectateur ne joue pour personne, et son retard ne
    // change rien pour les autres. Voir `transportDelayMs`.
    studio.setPlayoutDelayMs(transportDelayMs(delayMs, role));
  }, [delayMs, role, studio]);

  /**
   * Cale la vidéo et le son sur les notes : l'horloge de séance pour dater ce
   * qui part, et le délai que ce poste applique aux notes des autres pour
   * retenir ce qui arrive — horizon compris chez un spectateur. Une note, une
   * image et un son pris au même instant se montrent et s'entendent ainsi au
   * même instant chez chacun. Les moteurs ne retiennent pas les sources de ce
   * poste : l'interprète se regarde en direct. Le calage est partagé par les
   * deux moteurs : voir `lib/live/media-timing.ts`.
   *
   * `status` est là pour la séance neuve : son calage part de zéro, et
   * l'horloge doit l'y rejoindre dès la connexion.
   */
  useEffect(() => {
    sessionRef.current?.timing.setPlayout({ clockOffsetMs, remoteDelayMs: transportDelayMs(delayMs, role) });
  }, [clockOffsetMs, delayMs, role, status]);

  /**
   * Dit au clavier s'il émet, ce qui décide où il capte.
   *
   * Un émetteur capte au présent vrai, `D` devant sa tête de lecture : son
   * tick doit être encore devant chez le destinataire. Un auditeur capte à sa
   * tête : son tick ne part chez personne, et capter en avance lui ferait
   * entendre son propre jeu avec son propre décalage, pour rien. Le leader n'a
   * pas de cas particulier — son délai vaut zéro, les deux ticks coïncident.
   *
   * Séparé de l'effet du délai parce que les deux ne bougent pas ensemble :
   * le délai change à chaque annonce de jambe, le droit d'émettre à chaque
   * changement de lead ou de régime.
   */
  useEffect(() => {
    if (studio === null) {
      return;
    }

    studio.setLiveEmitting(emitting);
  }, [emitting, studio]);

  /**
   * Aucun `setState` avant le premier `await` : cette fonction est appelée
   * depuis un effet, et y muter l'état de façon synchrone déclencherait un
   * rendu en cascade. Tout ce qu'elle change passe par une continuation.
   */
  const join = useCallback(
    async (target: string) => {
      try {
        const ticket = await joinLiveSession(target);
        setError(null);

        const recital = ticket.session?.recital ?? null;

        // Un récital ne se rejoint que par sa salle d'attente. Un ancien lien
        // de séance, ou une salle trouvée ailleurs, y renvoie avant toute
        // connexion : inscription, places comptées et clic qui réveille le son
        // passent par là. La tentative est oubliée, pour que l'entrée par la
        // salle d'attente, qui mène à la même salle, puisse partir.
        if (recital !== null && recitalEntryRoom(recital.id) !== ticket.room) {
          joinAttemptedRef.current = null;
          setStatus("off");
          router.replace(recitalPath(recital.id), { scroll: false });

          return;
        }

        await connect(ticket);

        // L'adresse d'un récital porte le récital, pas la salle : l'interprète
        // copie sa barre d'adresse et la partage, et elle mène à la salle
        // d'attente chez qui n'y est pas entré. Voir `recitalStudioUrl`.
        if (recital !== null) {
          router.replace(recitalStudioUrl("", recital.id), { scroll: false });
        }
      } catch (cause) {
        await teardown();
        setStatus("error");
        setError(cause instanceof Error ? cause.message : "Impossible de rejoindre ce live.");
      }
    },
    [connect, router, teardown],
  );

  /**
   * Ouvre une séance pour jouer à plusieurs, ou demande un compte.
   *
   * # On reste dans le studio
   *
   * Le morceau ouvert, la vue, le clavier branché : rien ne bouge. L'adresse
   * prend seulement la salle (`?live=`), pour qu'un rechargement y ramène et
   * que la barre d'adresse soit le lien à partager. La tentative est posée
   * **avant** l'adresse : l'effet de jointure, qui se réveille sur elle, la
   * reconnaît comme déjà faite et ne demande pas un second billet — deux
   * connexions sous la même identité, LiveKit expulserait la première.
   *
   * Tant que le compte n'est pas résolu, on ne fait rien plutôt que de
   * demander : `isAuthenticated` vaut `false` pendant le premier tour, et
   * ouvrir l'écran là-dessus le montrerait à quelqu'un qui est déjà connecté.
   */
  const invite = useCallback(async () => {
    if (authLoading) {
      return false;
    }

    if (!isAuthenticated) {
      openAccountModal("live_jam");

      return false;
    }

    setError(null);
    setStatus("opening");

    try {
      const ticket = await openLiveSession();

      joinAttemptedRef.current = ticket.room;
      await connect(ticket);
      router.replace(liveStudioPath(pathname, ticket.room), { scroll: false });

      return true;
    } catch (cause) {
      joinAttemptedRef.current = null;
      await teardown();
      setStatus("error");
      setError(cause instanceof Error ? cause.message : "La séance n'a pas pu s'ouvrir.");

      return false;
    }
  }, [authLoading, connect, isAuthenticated, openAccountModal, pathname, router, teardown]);

  const enter = useCallback(
    (target: string) => {
      router.replace(liveStudioPath(pathname, target), { scroll: false });
    },
    [pathname, router],
  );

  const leave = useCallback(async () => {
    /*
      Lu **avant** `teardown`, qui efface le ticket.

      Quitter sa propre séance la ferme : l'API pose `closed_at` et rend le
      code à la réserve. Sans ce geste, la ligne resterait ouverte jusqu'à ce
      que le service constate la salle vide — et entre les deux, le code
      continuerait de mener à une salle où il n'y a plus personne, ce qui est
      le seul échec que l'écran du code ne sait pas expliquer.

      Seulement `mine` : quelqu'un qui sort d'une séance qu'il suivait ne ferme
      pas la maison de son hôte.
    */
    const owned = ticketRef.current?.session ?? null;

    // Le récital s'arrête pour ses téléphones aussi. Oublié, un téléphone
    // filmerait pour une salle fermée, et la tiendrait ouverte : LiveKit ne
    // ferme pas une salle où quelqu'un est encore, et le récital resterait
    // « en direct ».
    const video = sessionRef.current?.video;

    if (ticketRef.current?.role === "performer" && video !== undefined) {
      await Promise.race([
        Promise.all(pairedDevices(video.sources.value, video.self).map((phone) => removeCamera(phone.id))),
        new Promise<void>((resolve) => window.setTimeout(resolve, LEAVE_CAMERA_CUT_MS)),
      ]);
    }

    await exit(null);

    if (owned !== null && owned.mine) {
      /*
        Sans `await` et sans remontée d'erreur : on est déjà parti, la scène est
        rendue, et faire échouer un départ parce que la fermeture n'a pas abouti
        laisserait quelqu'un coincé dans un panneau. Le service finira de toute
        façon par constater la salle vide. On journalise, parce qu'une
        fermeture qui rate en série est un vrai symptôme.

        C'est aussi ce geste qui **sort le public** : l'API fait supprimer la
        salle par le service de temps, et chacun est déconnecté là où il est.
        Un public qui resterait tiendrait la salle ouverte — LiveKit ne ferme
        que les salles vides — et le récital resterait « en direct ».
      */
      void closeLiveSession(owned.room).catch((cause: unknown) => {
        console.warn("Fermeture de la séance live :", cause);
      });
    }
  }, [exit, removeCamera]);

  const recording = !isListener && session?.recital != null ? session.recital.recording : null;
  const doorsOpen = session?.recital != null ? session.recital.open : null;

  /*
    Ouvrir au public, depuis le studio.

    L'interprète est monté sur scène pour brancher sa caméra et se cadrer ; ce
    geste-ci fait tout le reste d'un coup — la porte s'ouvre aux spectateurs, le
    récital passe « en direct », l'enregistrement commence (décidé le
    2026-09-18 : l'enregistrement commençait avec le récital, donc avec la
    préparation). L'API rend ce que l'enregistreur a répondu : la marque
    « enregistré » ne doit pas mentir.
  */
  const openDoors = useCallback(async () => {
    const ticket = ticketRef.current;
    const recital = ticket?.session?.recital ?? null;

    if (ticket?.role !== "performer" || recital === null || recital.open) {
      return false;
    }

    try {
      const opened = await openRecital(recital.id);

      setSession((current) =>
        current?.recital != null
          ? { ...current, recital: { ...current.recital, open: true, recording: opened.recording } }
          : current,
      );

      return true;
    } catch (cause) {
      console.warn("Ouverture du récital :", cause);

      return false;
    }
  }, []);

  const stopRecording = useCallback(async () => {
    const ticket = ticketRef.current;
    const recital = ticket?.session?.recital ?? null;

    if (ticket?.role !== "performer" || recital === null) {
      return false;
    }

    try {
      const next = await stopRecitalRecording(recital.id);

      // En état seulement : c'est ce que le panneau et le bouton lisent. Un
      // prochain billet l'apportera de l'API, arrêt compris.
      setSession((current) =>
        current?.recital != null ? { ...current, recital: { ...current.recital, recording: next } } : current,
      );

      return true;
    } catch (cause) {
      console.warn("Arrêt de l'enregistrement :", cause);

      return false;
    }
  }, []);

  const copyShareUrl = useCallback(async () => {
    if (shareUrl === null) {
      return false;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);

      return true;
    } catch {
      // Presse-papier refusé : contexte non sûr, ou permission déclinée. Le
      // bouton retombe sur l'affichage du lien, qui reste sélectionnable.
      return false;
    }
  }, [shareUrl]);

  // Jointure automatique depuis l'URL. La garde sur `joinAttemptedRef` couvre
  // le cas où il ne faut rien faire : on est déjà dans cette salle, ou en
  // train d'y entrer.
  //
  // Un récital s'annonce par son identifiant et non par sa salle : la salle
  // vient de ce que la salle d'attente a retenu en y faisant entrer cet
  // onglet. Sans elle, l'adresse renvoie à la salle d'attente.
  const requestedRecital = recitalIdFromParams(searchParams);
  const requestedRoom = liveRoomFromParams(searchParams)
    ?? (requestedRecital === null ? null : recitalEntryRoom(requestedRecital));

  useEffect(() => {
    if (requestedRecital !== null && requestedRoom === null && isStudioPathname(pathname)) {
      router.replace(recitalPath(requestedRecital), { scroll: false });

      return;
    }

    if (requestedRoom === null || joinAttemptedRef.current === requestedRoom) {
      return;
    }

    // Hors du studio, le paramètre ne veut rien dire : le piano n'est pas à
    // l'écran, se connecter ferait entrer un participant muet et invisible.
    if (!isStudioPathname(pathname)) {
      return;
    }

    // Pas avant que le studio soit monté. `connect` ne branche la séance —
    // session de studio, pont d'émission, piste des partenaires — que s'il
    // tient un studio, et il le lit dans sa fermeture. Or `StudioProvider`
    // construit le sien dans un effet : au premier passage ici, `studio` est
    // encore `null`. Rejoindre à ce moment-là donne une connexion qui tient,
    // des participants et des latences qui s'affichent, et **aucune note dans
    // aucun sens** — et le garde-fou ci-dessous, posé sur cette tentative-là,
    // interdisait ensuite la bonne. On attend donc : l'effet rejoue quand
    // `join` change d'identité, c'est-à-dire quand le studio arrive.
    if (studio === null) {
      return;
    }

    // Le compte, avant le billet.
    //
    // Rien tant qu'on ne sait pas : `isAuthenticated` vaut `false` pendant que
    // la session s'établit, et demander là-dessus montrerait l'écran de compte
    // à quelqu'un qui en a déjà un. L'effet rejoue quand la réponse arrive.
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      // L'état de départ dit « en train de se connecter », parce que l'URL le
      // dit. Ce n'est plus vrai : sans compte, rien ne part, et laisser le
      // bouton sur « Connexion… » ferait tourner une attente sans fin.
      setStatus("off");

      // `joinAttemptedRef` reste vide : on n'a pas tenté, on a refusé. C'est
      // ce qui fait que la jointure part toute seule après l'inscription,
      // l'effet rejouant sur `isAuthenticated`.
      // Deux portes, deux promesses : un récital, on vient le regarder ; une
      // séance sans récital, on vient y jouer.
      if (authPromptedRef.current !== requestedRoom) {
        authPromptedRef.current = requestedRoom;
        openAccountModal(requestedRecital === null ? "live_jam" : "live_session");
      }

      return;
    }

    // Synchrone, avant tout `await` : c'est ce qui rend le garde-fou efficace.
    joinAttemptedRef.current = requestedRoom;

    void join(requestedRoom);
  }, [
    authLoading,
    isAuthenticated,
    join,
    openAccountModal,
    pathname,
    requestedRecital,
    requestedRoom,
    router,
    studio,
  ]);

  /**
   * Renouvelle le billet quand le nom du compte change pendant un live.
   *
   * Le nom affiché dans la liste des participants est signé dans le jeton à
   * l'entrée, et le service de temps le relit à chaque annonce. Renommer son
   * compte ne touchait donc rien : l'ancien nom restait, chez soi comme chez
   * les autres, jusqu'à la sortie. On redemande un billet pour la même salle,
   * avec la même identité ; l'annonce suivante porte le nouveau nom, et le
   * service le rend à tout le monde en quelques secondes. La connexion
   * LiveKit n'est pas refaite.
   */
  const nickname = user?.nickname ?? null;
  const seenNicknameRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const previous = seenNicknameRef.current;
    seenNicknameRef.current = nickname;

    const ticket = ticketRef.current;

    if (previous === undefined || previous === nickname || ticket === null) {
      return;
    }

    let superseded = false;

    joinLiveSession(ticket.room, { renew: true })
      .then((fresh) => {
        const current = ticketRef.current;

        // Un renommage plus récent a pris la main, ou l'on a quitté la salle.
        if (superseded || current === null || current.room !== fresh.room || current.identity !== fresh.identity) {
          return;
        }

        ticketRef.current = { ...current, token: fresh.token, name: fresh.name, expiresAt: fresh.expiresAt };
      })
      .catch((cause: unknown) => {
        console.warn("[live] renouvellement du billet après un changement de nom :", cause);
      });

    return () => {
      superseded = true;
    };
  }, [nickname]);

  useEffect(() => {
    return () => {
      void teardown();
    };
  }, [teardown]);

  /**
   * La latence locale, relue à chaque rendu mais rendue **par identité** tant
   * que ses deux nombres ne bougent pas : un objet neuf ici faisait changer la
   * valeur du contexte, donc re-rendre tous ses consommateurs, à chaque rendu
   * du fournisseur — sans qu'aucune donnée lisible ait changé.
   */
  const localLatencyRef = useRef<LocalAudioLatency | null>(null);
  /*
    Sauf au tout premier rendu client, qui doit rendre ce que le serveur a
    rendu. `getOutputLatency()` interroge le contexte audio : zéro côté
    serveur, quelques dizaines de millisecondes côté client dès que le moteur
    a démarré — et il démarre avant que ce sous-arbre s'hydrate. Les deux
    rendus affichaient donc deux chiffres, et React jetait tout le Studio pour
    le refaire. `useHydrated` repasse à `true` juste après l'hydratation, donc
    la vraie valeur arrive au battement suivant, sans rien perdre de la
    fraîcheur que ce calcul cherche.
  */
  const hydrated = useHydrated();
  const nextLocalLatency = hydrated ? readLocalLatency() : SERVER_LATENCY;
  const previousLocalLatency = localLatencyRef.current;
  const localLatency =
    previousLocalLatency !== null &&
    previousLocalLatency.lookAheadMs === nextLocalLatency.lookAheadMs &&
    previousLocalLatency.outputMs === nextLocalLatency.outputMs
      ? previousLocalLatency
      : nextLocalLatency;
  localLatencyRef.current = localLatency;

  const value = useMemo<LiveSessionContextValue>(
    () => ({
      status,
      room,
      shareUrl,
      session,
      participants,
      headcount,
      fetchParticipants,
      hall,
      identity,
      role,
      isListener,
      jam,
      setJam,
      lead,
      isLeader,
      setLead,
      measuresHidden,
      setMeasuresHidden,
      cameras,
      setCameraFraming,
      watchVideo,
      fetchDeclaredCameras: fetchCameras,
      curtain,
      setCurtain,
      chatClosed,
      setChatClosed,
      take,
      nextTake,
      emitting,
      delayMs,
      sessionState,
      clockOffsetMs,
      clockDriftMs,
      localLatency,
      error,
      startFailure,
      recording,
      stopRecording,
      doorsOpen,
      openDoors,
      setTransportPaused,
      invite,
      enter,
      leave,
      copyShareUrl,
      videoSources,
      videoSelf,
      localCamera,
      phonePairing,
      cameraError,
      startWebcam,
      stopWebcam,
      pairPhone,
      cancelPhonePairing,
      removeCamera,
    }),
    [
      status,
      room,
      shareUrl,
      session,
      participants,
      headcount,
      fetchParticipants,
      hall,
      identity,
      role,
      isListener,
      jam,
      setJam,
      lead,
      isLeader,
      setLead,
      measuresHidden,
      setMeasuresHidden,
      cameras,
      setCameraFraming,
      watchVideo,
      fetchCameras,
      curtain,
      setCurtain,
      chatClosed,
      setChatClosed,
      take,
      nextTake,
      emitting,
      delayMs,
      sessionState,
      clockOffsetMs,
      clockDriftMs,
      localLatency,
      error,
      startFailure,
      recording,
      stopRecording,
      doorsOpen,
      openDoors,
      setTransportPaused,
      invite,
      enter,
      leave,
      copyShareUrl,
      videoSources,
      videoSelf,
      localCamera,
      phonePairing,
      cameraError,
      startWebcam,
      stopWebcam,
      pairPhone,
      cancelPhonePairing,
      removeCamera,
    ],
  );

  return <LiveSessionContext.Provider value={value}>{children}</LiveSessionContext.Provider>;
}

/**
 * Rend le contexte live, ou `null` hors de son fournisseur.
 *
 * Volontairement tolérant, à l'inverse d'`useStudio` qui lève : le live est une
 * fonctionnalité que des écrans peuvent ignorer, et un bouton qui se retire
 * silencieusement vaut mieux qu'une page blanche.
 */
export function useLiveSession(): LiveSessionContextValue | null {
  return useContext(LiveSessionContext);
}

function toStatus(state: LiveConnectionState): LiveStatus {
  if (state === "connected") {
    return "live";
  }

  if (state === "connecting") {
    return "connecting";
  }

  if (state === "reconnecting") {
    return "reconnecting";
  }

  if (state === "failed") {
    return "error";
  }

  return "off";
}
